import useMobileSchedule from '../../../hooks/useMobileSchedule';
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { useParams, useLocation } from "react-router-dom";
import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef, useTransition, useDeferredValue } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../contexts/AuthContext';

import {
    FiUser, FiUserX, FiHash, FiCalendar, FiClock, FiTool, FiDollarSign,
    FiHome, FiLayers, FiCheckCircle, FiAlertCircle,
    FiX, FiChevronRight, FiGrid, FiChevronDown, FiList, FiSend, FiFilter, FiEyeOff,
    FiMessageSquare
} from "react-icons/fi";
import { GiWoodBeam, GiWoodPile } from "react-icons/gi";
import { BrickWall, Grid2X2, Hash, ArrowDownUp, ArrowUpNarrowWide, ArrowDownNarrowWide, Flame, ArrowDownAZ, ArrowUpAZ, ClockArrowUp, SquareStack } from "lucide-react";

import AssignmentContextMenu from './AssignmentContextMenu';
import './ContextMenu.css';
import { searchJobs } from '../../../lib/supabase/assignments';
import { assignDesignerToJob, toggleHold, toggleQuoteHold, toggleSentProduction } from '../../../lib/supabase/assignments';
import { updateJobEditorNote, updateQuoteEditorNote } from '../../../lib/supabase/schedule';
import { createNotification, NOTIFICATION_TYPES } from "../../../lib/supabase/notifications";
import { forceFinish } from '../../../lib/supabase/force-finish';
import Toast from '../../common/Toast';
import { getJobStatusSummary, getDesignStatusText } from '../../../lib/common/jobStatusText';
import EditAssignment from '../../assignments/EditAssignment';
import { getInvoiceStatusText, getInvoiceSourceIcon } from '../../../lib/common/invoiceDisplay';


import {
    getEnabledExtraQuoteTypes,
    getQuoteLabel,
    isFileOnlyQuoteType,
    QUOTE_ORDER,
} from "../../../lib/quoteTypes";

const ColumnToggle = ({
    searchQuery,
    setSearchQuery,
    columnVisibility,
    setColumnVisibility,
    setColumnSizing,
    selectedQuoteTypes,
    setSelectedQuoteTypes,
    invoiceFilter,
    setInvoiceFilter,
    quoteSentFilter,
    setQuoteSentFilter,
    urgentFilter,
    setUrgentFilter,
    jobTypeFilter,
    setJobTypeFilter,
    hideEstimationCompleted,
    setHideEstimationCompleted,
    hideEstimatorAssigned,
    setHideEstimatorAssigned,
    sortBy,
    setSortBy,
    cancelledFilter,
    setCancelledFilter,
    quoteTypeFilterOptions = [],
}) => {
    const [mobileControlsOpen, setMobileControlsOpen] = useState(false);
    const [showColumnVisDropdown, setShowColumnVisDropdown] = useState(false);
    const [showTypeDropdown, setShowTypeDropdown] = useState(false);
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [showQuoteTypeDropdown, setShowQuoteTypeDropdown] = useState(false);
    const [showInvoiceDropdown, setShowInvoiceDropdown] = useState(false);
    const [showQuoteSentDropdown, setShowQuoteSentDropdown] = useState(false);
    const [showUrgentDropdown, setShowUrgentDropdown] = useState(false);
    const columnVisDropdownRef = useRef(null);
    const typeDropdownRef = useRef(null);
    const sortDropdownRef = useRef(null);
    const quoteTypeDropdownRef = useRef(null);
    const invoiceDropdownRef = useRef(null);
    const quoteSentDropdownRef = useRef(null);
    const urgentDropdownRef = useRef(null);

    const [showCancelledDropdown, setShowCancelledDropdown] = useState(false);
    const cancelledDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnVisDropdownRef.current && !columnVisDropdownRef.current.contains(event.target)) setShowColumnVisDropdown(false);
            if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) setShowTypeDropdown(false);
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) setShowSortDropdown(false);
            if (quoteTypeDropdownRef.current && !quoteTypeDropdownRef.current.contains(event.target)) setShowQuoteTypeDropdown(false);
            if (invoiceDropdownRef.current && !invoiceDropdownRef.current.contains(event.target)) setShowInvoiceDropdown(false);
            if (quoteSentDropdownRef.current && !quoteSentDropdownRef.current.contains(event.target)) setShowQuoteSentDropdown(false);
            if (urgentDropdownRef.current && !urgentDropdownRef.current.contains(event.target)) setShowUrgentDropdown(false);
            if (cancelledDropdownRef.current && !cancelledDropdownRef.current.contains(event.target)) {
                setShowCancelledDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="editor-controls mb-4 space-y-3">
            {/* Search Bar */}
            <div className="relative">
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by Job ID, Customer, Assignee, Status..."
                    className="w-full px-4 py-2.5 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
                <FiGrid className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                {searchQuery && (
                    <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <FiX className="w-4 h-4" />
                    </button>
                )}
            </div>

            <button
                type="button"
                className="editor-controls-toggle hidden"
                aria-expanded={mobileControlsOpen}
                onClick={() => setMobileControlsOpen(open => !open)}
            >
                <FiFilter aria-hidden="true" />
                Filters & Columns
                <FiChevronDown aria-hidden="true" className={mobileControlsOpen ? 'rotate-180' : ''} />
            </button>
            {/* Column Toggle Section */}
            <div className={`editor-advanced-controls ${mobileControlsOpen ? 'is-open' : ''} bg-white border border-gray-200 rounded-lg shadow-sm`}>
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <FiList className="w-4 h-4 text-gray-600" />
                            <span className="text-sm font-semibold text-gray-700">Table Preferences</span>
                        </div>
                        <div className="flex items-center gap-3">
                            {/* Column visibility dropdown */}
                            <div className="relative" ref={columnVisDropdownRef}>
                                <button
                                    onClick={() => setShowColumnVisDropdown(!showColumnVisDropdown)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-white text-gray-700 border-gray-200 hover:border-gray-300 transition-all"
                                >
                                    <FiGrid className="w-3 h-3" />
                                    Columns
                                    {Object.values(columnVisibility).some(v => v === false) && (
                                        <span className="bg-blue-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                                            {Object.values(columnVisibility).filter(Boolean).length}
                                        </span>
                                    )}
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${showColumnVisDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {showColumnVisDropdown && (
                                    <div className="absolute right-0 mt-1.5 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-[60]">
                                        <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Columns</span>
                                            <button
                                                onClick={() => {
                                                    const allVisible = Object.values(columnVisibility).every(Boolean);
                                                    const newVisibility = Object.keys(columnVisibility).reduce((acc, key) => {
                                                        acc[key] = !allVisible;
                                                        return acc;
                                                    }, {});
                                                    setColumnVisibility(newVisibility);
                                                }}
                                                className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                                            >
                                                {Object.values(columnVisibility).every(Boolean) ? 'Hide All' : 'Show All'}
                                            </button>
                                        </div>
                                        <div className="p-2 space-y-0.5">
                                            {[
                                                { key: 'uniqueId', label: 'Job ID', icon: FiHash },
                                                { key: 'customerName', label: 'Customer', icon: FiUser },
                                                { key: 'jobType', label: 'Type', icon: FiTool },
                                                { key: 'mainStatus', label: 'Status', icon: FiClock },
                                                { key: 'quotes', label: 'Quotes', icon: FiLayers },
                                                { key: 'assignee', label: 'Assignee', icon: FiUser },
                                                { key: 'priority', label: 'Priority', icon: FiAlertCircle },
                                                { key: 'invoiced', label: 'Invoice', icon: FiDollarSign },
                                                { key: 'quoteSent', label: 'Quote Sent', icon: FiSend },
                                                { key: 'createdAt', label: 'Created', icon: FiCalendar },
                                                { key: 'etaDate', label: 'ETA Date', icon: FiCalendar },
                                                { key: 'note', label: 'Note', icon: FiMessageSquare },
                                            ].map(({ key, label, icon: Icon }) => {
                                                const isOn = columnVisibility[key] ?? true;
                                                return (
                                                    <button
                                                        key={key}
                                                        onClick={() => setColumnVisibility(prev => ({ ...prev, [key]: !isOn }))}
                                                        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs transition-colors ${isOn
                                                            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                                            : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                                                            }`}
                                                    >
                                                        <Icon className="w-3.5 h-3.5 shrink-0" />
                                                        <span className="flex-1 text-left font-medium">{label}</span>
                                                        <div className={`w-7 h-4 rounded-full transition-colors shrink-0 ${isOn ? 'bg-blue-500' : 'bg-gray-200'}`}>
                                                            <div className={`w-3 h-3 bg-white rounded-full shadow mt-0.5 transition-transform ${isOn ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={() => {
                                    setColumnVisibility({
                                        uniqueId: true, customerName: true, jobType: true,
                                        mainStatus: true, quotes: true, assignee: true,
                                        priority: true, createdAt: true, invoiced: true,
                                        quoteSent: true, etaDate: true, note: true
                                    });
                                    setColumnSizing({});
                                }}
                                className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                            >
                                Reset Table
                            </button>
                        </div>
                    </div>
                </div>

                {/* Quote Type Filters */}
                <div className="editor-filter-groups flex items-start gap-4 flex-row p-4">
                    <div className='flex flex-col gap-2'>
                        <span className="text-xs font-medium text-gray-600">Filter by Quote Type:</span>
                        <div className='flex flex-row gap-2 flex-wrap'>
                            <div className="relative" ref={cancelledDropdownRef}>
                                <button
                                    onClick={() => setShowCancelledDropdown(!showCancelledDropdown)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border ${cancelledFilter === 'cancelled_only'
                                        ? 'bg-red-50 text-red-700 border-red-200'
                                        : cancelledFilter === 'active_cancelled'
                                            ? 'bg-orange-50 text-orange-700 border-orange-200'
                                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <FiAlertCircle className="w-3 h-3" />
                                    Jobs: {
                                        cancelledFilter === 'active_only'
                                            ? 'Active Only'
                                            : cancelledFilter === 'active_cancelled'
                                                ? 'Active + Cancelled'
                                                : 'Cancelled Only'
                                    }
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${showCancelledDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {showCancelledDropdown && (
                                    <div className="absolute left-0 mt-1.5 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                        <div className="p-1.5 space-y-0.5">
                                            {[
                                                { value: 'active_only', label: 'Active Only', color: 'hover:bg-gray-50 text-gray-700' },
                                                { value: 'active_cancelled', label: 'Active + Cancelled', color: 'hover:bg-orange-50 text-orange-700' },
                                                { value: 'cancelled_only', label: 'Cancelled Only', color: 'hover:bg-red-50 text-red-700' },
                                            ].map(({ value, label, color }) => (
                                                <button
                                                    key={value}
                                                    onClick={() => {
                                                        setCancelledFilter(value);
                                                        setShowCancelledDropdown(false);
                                                    }}
                                                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-medium transition-colors ${color} ${cancelledFilter === value ? 'font-semibold' : ''}`}
                                                >
                                                    {label}
                                                    {cancelledFilter === value && <FiCheckCircle className="w-3.5 h-3.5 opacity-70" />}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="relative" ref={quoteTypeDropdownRef}>
                                <button
                                    onClick={() => setShowQuoteTypeDropdown(!showQuoteTypeDropdown)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                                >
                                    <FiLayers className="w-3 h-3" />
                                    Quote Types
                                    {Object.values(selectedQuoteTypes).some(v => !v) && (
                                        <span className="bg-blue-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                                            {Object.values(selectedQuoteTypes).filter(Boolean).length}
                                        </span>
                                    )}
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${showQuoteTypeDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {showQuoteTypeDropdown && (
                                    <div className="absolute left-0 mt-1.5 w-52 bg-white border border-gray-200 rounded-lg shadow-xl z-[60]">
                                        <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Quote Types</span>
                                            <button
                                                onClick={() => setSelectedQuoteTypes(
                                                    quoteTypeFilterOptions.reduce((acc, item) => {
                                                        acc[item.key] = true;
                                                        return acc;
                                                    }, {})
                                                )}
                                                className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                                            >
                                                Show All
                                            </button>
                                        </div>
                                        <div className="p-2 space-y-0.5">
                                            {quoteTypeFilterOptions.map(({ key, label, Icon }) => {
                                                const isOn = selectedQuoteTypes[key];
                                                return (
                                                    <button
                                                        key={key}
                                                        onClick={() => setSelectedQuoteTypes(prev => ({ ...prev, [key]: !isOn }))}
                                                        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs transition-colors ${isOn
                                                            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                                            : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                                                            }`}
                                                    >
                                                        <Icon className="w-3.5 h-3.5 shrink-0" />
                                                        <span className="flex-1 text-left font-medium">{label}</span>
                                                        <div className={`w-7 h-4 rounded-full transition-colors shrink-0 ${isOn ? 'bg-blue-500' : 'bg-gray-200'}`}>
                                                            <div className={`w-3 h-3 bg-white rounded-full shadow mt-0.5 transition-transform ${isOn ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <hr className='text-gray-200 bg-gray-200 w-0.5 h-12 rounded-full self-center' />

                    <div className='flex flex-col gap-2'>

                        <span className="text-xs font-medium text-gray-600">Filter by Invoice:</span>
                        <div className='flex flex-row gap-2 flex-wrap'>

                            {/* Invoice dropdown */}
                            <div className="relative" ref={invoiceDropdownRef}>
                                <button
                                    onClick={() => setShowInvoiceDropdown(!showInvoiceDropdown)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border ${invoiceFilter === 'all_sent' ? 'bg-green-50 text-green-700 border-green-200' :
                                        invoiceFilter === 'partial' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                            invoiceFilter === 'not_sent' ? 'bg-red-50 text-red-700 border-red-200' :
                                                'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <FiDollarSign className="w-3 h-3" />
                                    Invoice: {invoiceFilter === 'all' ? 'All' : invoiceFilter === 'all_sent' ? 'All Sent' : invoiceFilter === 'partial' ? 'Partial' : 'Not Sent'}
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${showInvoiceDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                {showInvoiceDropdown && (
                                    <div className="absolute left-0 mt-1.5 w-44 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                        <div className="p-1.5 space-y-0.5">
                                            {[
                                                { value: 'all', label: 'All', color: 'hover:bg-gray-50 text-gray-700' },
                                                { value: 'all_sent', label: 'All Sent', color: 'hover:bg-green-50 text-green-700' },
                                                { value: 'partial', label: 'Partial', color: 'hover:bg-yellow-50 text-yellow-700' },
                                                { value: 'not_sent', label: 'Not Sent', color: 'hover:bg-red-50 text-red-700' },
                                            ].map(({ value, label, color }) => (
                                                <button key={value} onClick={() => { setInvoiceFilter(value); setShowInvoiceDropdown(false); }}
                                                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-medium transition-colors ${color} ${invoiceFilter === value ? 'font-semibold' : ''}`}>
                                                    {label}
                                                    {invoiceFilter === value && <FiCheckCircle className="w-3.5 h-3.5 opacity-70" />}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <hr className='text-gray-200 bg-gray-200 w-0.5 h-12 rounded-full self-center' />

                    <div className='flex flex-col gap-2'>
                        <span className="text-xs font-medium text-gray-600">Estimation Filters:</span>
                        <div className='flex flex-row gap-2 flex-wrap'>

                            {/* Quote Sent dropdown */}
                            <div className="relative" ref={quoteSentDropdownRef}>
                                <button
                                    onClick={() => setShowQuoteSentDropdown(!showQuoteSentDropdown)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border ${quoteSentFilter === 'sent' ? 'bg-green-50 text-green-700 border-green-200' :
                                        quoteSentFilter === 'not_sent' ? 'bg-red-50 text-red-700 border-red-200' :
                                            'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <FiSend className="w-3 h-3" />
                                    Quote: {quoteSentFilter === 'all' ? 'All' : quoteSentFilter === 'sent' ? 'Sent' : 'Not Sent'}
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${showQuoteSentDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                {showQuoteSentDropdown && (
                                    <div className="absolute left-0 mt-1.5 w-40 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                        <div className="p-1.5 space-y-0.5">
                                            {[
                                                { value: 'all', label: 'All', color: 'hover:bg-gray-50 text-gray-700' },
                                                { value: 'sent', label: 'Sent', color: 'hover:bg-green-50 text-green-700' },
                                                { value: 'not_sent', label: 'Not Sent', color: 'hover:bg-red-50 text-red-700' },
                                            ].map(({ value, label, color }) => (
                                                <button key={value} onClick={() => { setQuoteSentFilter(value); setShowQuoteSentDropdown(false); }}
                                                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-medium transition-colors ${color} ${quoteSentFilter === value ? 'font-semibold' : ''}`}>
                                                    {label}
                                                    {quoteSentFilter === value && <FiCheckCircle className="w-3.5 h-3.5 opacity-70" />}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Urgent dropdown */}
                            <div className="relative" ref={urgentDropdownRef}>
                                <button
                                    onClick={() => setShowUrgentDropdown(!showUrgentDropdown)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border ${urgentFilter === 'urgent_quote' ? 'bg-red-50 text-red-700 border-red-200' :
                                        urgentFilter === 'urgent_design' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                            'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <FiAlertCircle className="w-3 h-3" />
                                    Urgent: {urgentFilter === 'all' ? 'All' : urgentFilter === 'urgent_quote' ? 'Quote' : 'Design'}
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${showUrgentDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                {showUrgentDropdown && (
                                    <div className="absolute left-0 mt-1.5 w-44 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                        <div className="p-1.5 space-y-0.5">
                                            {[
                                                { value: 'all', label: 'All', color: 'hover:bg-gray-50 text-gray-700' },
                                                { value: 'urgent_quote', label: 'Urgent Quote', color: 'hover:bg-red-50 text-red-700' },
                                                { value: 'urgent_design', label: 'Urgent Design', color: 'hover:bg-amber-50 text-amber-700' },
                                            ].map(({ value, label, color }) => (
                                                <button key={value} onClick={() => { setUrgentFilter(value); setShowUrgentDropdown(false); }}
                                                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-medium transition-colors ${color} ${urgentFilter === value ? 'font-semibold' : ''}`}>
                                                    {label}
                                                    {urgentFilter === value && <FiCheckCircle className="w-3.5 h-3.5 opacity-70" />}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Hide Estimation Completed toggle */}
                            <button
                                onClick={() => setHideEstimationCompleted(!hideEstimationCompleted)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border ${hideEstimationCompleted
                                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <FiEyeOff className="w-3 h-3" />
                                {hideEstimationCompleted ? 'Est. Completed Hidden' : 'Hide Est. Completed'}
                            </button>

                            {/* Hide Estimator Assigned toggle */}
                            <button
                                onClick={() => setHideEstimatorAssigned(!hideEstimatorAssigned)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border ${hideEstimatorAssigned
                                    ? 'bg-violet-50 text-violet-700 border-violet-200'
                                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <FiUserX className="w-3 h-3" />
                                {hideEstimatorAssigned ? 'Est. Assigned Hidden' : 'Hide Est. Assigned'}
                            </button>

                        </div>

                    </div>

                    <hr className='text-gray-200 bg-gray-200 w-0.5 h-12 rounded-full self-center' />

                    <div className='flex flex-col gap-2'>
                        <span className="text-xs font-medium text-gray-600">Filter by Type:</span>
                        <div className='flex flex-row gap-2 flex-wrap'>
                            {/* Job Type Filter Dropdown */}
                            <div className="relative" ref={typeDropdownRef}>
                                <button
                                    onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border ${jobTypeFilter === 'design_only'
                                        ? 'bg-teal-50 text-teal-700 border-teal-200'
                                        : jobTypeFilter === 'estimation_only'
                                            ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                                            : jobTypeFilter === 'design_estimation_only'
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                : jobTypeFilter === 'timber_only'
                                                    ? 'bg-lime-50 text-lime-700 border-lime-200'
                                                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <FiFilter className="w-3 h-3" />
                                    {jobTypeFilter === 'all' ? 'Type: All' :
                                        jobTypeFilter === 'design_only' ? 'Design Only' :
                                            jobTypeFilter === 'estimation_only' ? 'Estimation Only' :
                                                jobTypeFilter === 'design_estimation_only' ? 'Design & Estimation' :
                                                    'Timber Only'}
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {showTypeDropdown && (
                                    <div className="absolute left-0 mt-1.5 w-52 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                        <div className="p-1.5 space-y-0.5">
                                            {[
                                                { value: 'all', label: 'All Types', color: '' },
                                                { value: 'design_only', label: 'Design Only', color: '' },
                                                { value: 'estimation_only', label: 'Estimation Only', color: '' },
                                                { value: 'design_estimation_only', label: 'Design & Estimation', color: '' },
                                                { value: 'timber_only', label: 'Timber Only', color: '' },
                                            ].map(({ value, label, color }) => (
                                                <button
                                                    key={value}
                                                    onClick={() => { setJobTypeFilter(value); setShowTypeDropdown(false); }}
                                                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-medium transition-colors ${color} ${jobTypeFilter === value ? 'bg-opacity-100 font-semibold' : ''
                                                        }`}
                                                >
                                                    {label}
                                                    {jobTypeFilter === value && <FiCheckCircle className="w-3.5 h-3.5 opacity-70" />}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Sort Dropdown */}
                            <div className="relative" ref={sortDropdownRef}>
                                <button
                                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                                >
                                    <ArrowDownUp className="w-3 h-3" />
                                    {sortBy === 'newest' ? 'Newest First' :
                                        sortBy === 'oldest' ? 'Oldest First' :
                                            sortBy === 'priority' ? 'Priority First' :
                                                sortBy === 'alphabetical' ? 'A → Z' :
                                                    'Z → A'}
                                    <FiChevronDown className={`w-3 h-3 transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {showSortDropdown && (
                                    <div className="absolute left-0 mt-1.5 w-52 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                        <div className="p-1.5 space-y-0.5">
                                            {[
                                                { value: 'newest', label: 'Newest First', Icon: ArrowDownNarrowWide },
                                                { value: 'oldest', label: 'Oldest First', Icon: ArrowUpNarrowWide },
                                                { value: 'priority', label: 'Priority First', Icon: ClockArrowUp },
                                                { value: 'alphabetical', label: 'A → Z', Icon: ArrowDownAZ },
                                                { value: 'reverse_alphabetical', label: 'Z → A', Icon: ArrowUpAZ },
                                            ].map(({ value, label, Icon }) => (
                                                <button
                                                    key={value}
                                                    onClick={() => { setSortBy(value); setShowSortDropdown(false); }}
                                                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors hover:bg-gray-50 text-gray-700 ${sortBy === value ? 'bg-gray-100 font-semibold' : ''
                                                        }`}
                                                >
                                                    <Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                                                    <span className="flex-1 text-left">{label}</span>
                                                    {sortBy === value && <FiCheckCircle className="w-3.5 h-3.5 text-blue-500 shrink-0" />}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={() => {
                                    setInvoiceFilter('all');
                                    setQuoteSentFilter('all');
                                    setUrgentFilter('all');
                                    setJobTypeFilter('all');
                                    setHideEstimationCompleted(false);
                                    setHideEstimatorAssigned(false);
                                    setCancelledFilter('active_only');
                                }}
                                className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors ml-2"
                            >
                                Reset Filters
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Quote aggregation helper ─────────────────────────────────────────────────
/**
 * Get an aggregated quote object for a specific quote type from the quotes map.
 *
 * Handles both:
 *  - New style: quotes keyed by UUID, with quote_type field stored inside the value
 *    (produced by schedule.js transformJobData)
 *  - Old style: quotes keyed directly by type string (e.g. quotes.roof)
 *
 * When multiple quotes of the same type exist, aggregates them:
 *  - Status: worst-active wins (in_progress > waiting > completed)
 *  - Designers: flatMap all designers across all quotes of that type
 *  - invoiced: true only if ALL quotes of the type are invoiced
 *
 * @param {Object} quotesObj - assignment.quotes map
 * @param {string} quoteType - 'roof' | 'wall' | 'floor' | 'timber'
 * @returns {Object|null} aggregated quote object, or null if none are required
 */
const getAggregateQuoteByType = (quotesObj, quoteType) => {
    if (!quotesObj) return null;

    // Collect all quotes for this type (UUID-keyed: quote_type stored inside value)
    let list = Object.values(quotesObj).filter(
        q => q && (q.quote_type === quoteType || q.quoteType === quoteType)
    );

    // Old-style fallback: if nothing found by quote_type field, try direct key access
    if (list.length === 0 && quotesObj[quoteType]) {
        const q = quotesObj[quoteType];
        if (q?.isRequired || q?.is_required) list = [q];
    }

    // Filter to required quotes only
    const required = list.filter(q => q?.isRequired || q?.is_required);
    if (required.length === 0) return null;

    // Single quote — return as-is with _count added
    if (required.length === 1) return { ...required[0], _count: 1 };

    // Aggregate multiple quotes of this type — worst-active status wins
    const STATUS_PRIORITY = [
        'in_progress', 'waiting_assignment', 'waiting_design',
        'not_started', 'in_cutting', 'cutting_ended', 'in_production',
        'scheduled', 'in_transit', 'completed', 'not_required', 'not_scheduled',
    ];
    const aggStatus = (statuses) => {
        const valid = statuses.filter(s => s && s !== 'not_required');
        if (!valid.length) return statuses.find(Boolean) || 'not_required';
        return valid.reduce((best, cur) => {
            const bi = STATUS_PRIORITY.indexOf(best);
            const ci = STATUS_PRIORITY.indexOf(cur);
            if (ci === -1) return best;
            if (bi === -1) return cur;
            return ci < bi ? cur : best;
        });
    };

    const designStatus = aggStatus(required.map(q => q.designStatus || q.design_status));
    const estimationStatus = aggStatus(required.map(q => q.estimationStatus || q.estimation_status));

    return {
        isRequired: true,
        is_required: true,
        on_hold: required.some(q => q.on_hold),
        designStatus,
        design_status: designStatus,
        estimationStatus,
        estimation_status: estimationStatus,
        // invoiceSent: required.every(q => isQuoteInvoiceSent(q, invoiceCompany)),
        // invoice_state: required[0]?.invoice_state || required[0]?.invoice_status,
        // invoice_status: required[0]?.invoice_status || required[0]?.invoice_state,
        // xero_invoice_status: required[0]?.xero_invoice_status,
        department_file_status: required[0]?.department_file_status,
        departmentFileStatus: required[0]?.departmentFileStatus,
        department_files_completed_at: required[0]?.department_files_completed_at,
        departmentFilesCompletedAt: required[0]?.departmentFilesCompletedAt,
        files_count: required.reduce((sum, q) => sum + Number(q.files_count || q.file_count || q.filesCount || 0), 0),

        quote_type: quoteType,
        designers: required.flatMap(q => q.designers || []),
        production: required[0]?.production,
        delivery: required[0]?.delivery,
        child_unique_id: required[0]?.child_unique_id,
        _count: required.length,
    };
};

const SENT_NOTIFY_INVOICE_STATES = new Set(['invoiced', 'paid', 'cod', 'deposit']);
const SENT_XERO_INVOICE_STATES = new Set(['AUTHORISED', 'PAID']);

const isQuoteInvoiceSent = (quote, company) => {
    const mode = company?.settings?.invoice_display_mode || 'notify';

    if ((mode === 'xero' || mode === 'hybrid') && quote?.xero_invoice_status) {
        return SENT_XERO_INVOICE_STATES.has(quote.xero_invoice_status);
    }

    const notifyStatus =
        quote?.invoice_state ||
        quote?.invoice_status ||
        quote?.invoiceStatus ||
        'not_invoiced';

    return SENT_NOTIFY_INVOICE_STATES.has(notifyStatus);
};

const FILE_ONLY_RED = "bg-red-100 text-red-700 border-red-200";
const FILE_ONLY_GREEN = "bg-emerald-100 text-emerald-700 border-emerald-200";

const isFileOnlyQuoteCompleted = (quote) => {
    if (!quote) return false;

    return (
        quote.department_file_status === "completed" ||
        quote.departmentFileStatus === "completed" ||
        quote.department_files_completed_at ||
        quote.departmentFilesCompletedAt ||
        quote.files_count > 0 ||
        quote.file_count > 0 ||
        quote.filesCount > 0
    );
};

const getEditorQuoteIcon = (quoteType, size = 14) => {
    if (quoteType === "roof") return <FiHome size={size} />;
    if (quoteType === "wall") return <BrickWall size={size} />;
    if (quoteType === "floor") return <Grid2X2 size={size} />;
    if (quoteType === "timber") return <GiWoodBeam size={size} />;
    if (quoteType === "beams") return <GiWoodPile size={size} />;
    if (quoteType === "flooring_sheets") return <SquareStack size={size} />;

    return <FiLayers size={size} />;
};

const getEditorQuoteColor = (quote) => {
    const quoteType = quote?.quote_type || quote?.quoteType;

    if (isFileOnlyQuoteType(quoteType)) {
        return isFileOnlyQuoteCompleted(quote)
            ? FILE_ONLY_GREEN
            : FILE_ONLY_RED;
    }

    const status = quote?.designStatus || quote?.design_status;

    switch (status) {
        case "completed":
            return "bg-emerald-100 text-emerald-700 border-emerald-200";
        case "in_progress":
            return "bg-amber-100 text-amber-700 border-amber-200";
        case "waiting_design":
            return "bg-blue-100 text-blue-700 border-blue-200";
        case "waiting_assignment":
            return "bg-gray-100 text-gray-500 border-gray-200";
        case "not_required":
            return "bg-slate-50 text-slate-400 border-slate-200";
        default:
            return "bg-gray-100 text-gray-500 border-gray-200";
    }
};
// ─────────────────────────────────────────────────────────────────────────────

const JobEditorTable = forwardRef(function JobEditorTable({
    assignments,
    userType,
    company,
    invoiceCompany,
    navigate,
    availableDesigners,
    onLoadMore,
    hasMore,
    isLoadingMore,
    isLoading = false,
    scheduleType,
    showOnlyMine,
    selectedUser,
    companyId,
    sortBy = 'newest',
    setSortBy = () => { },
    jobTypeFilter = 'all',
    setJobTypeFilter = () => { },
    urgentFilter = 'all',
    setUrgentFilter = () => { },
    quoteSentFilter = 'all',
    setQuoteSentFilter = () => { },
    hideEstimationCompleted = false,
    setHideEstimationCompleted = () => { },
    hideEstimatorAssigned = false,
    setHideEstimatorAssigned = () => { },
    onDuplicateJob = () => { },
    onRefresh = () => { },
    cancelledFilter = 'active_only',
    setCancelledFilter = () => { },
    selectedQuoteTypes = {},
    setSelectedQuoteTypes = () => { },
    invoiceFilter = 'all',
    setInvoiceFilter = () => { },
}, ref) {
    const isMobile = useMobileSchedule();
    const [viewMode, setViewMode] = useState('combined');
    const [expandedAssignments, setExpandedAssignments] = useState([]);
    const [searchQuery, setSearchQuery] = useState(() => {
        try { return JSON.parse(localStorage.getItem('jobEditor_searchQuery') ?? '""'); } catch { return ''; }
    });
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [filteredAssignments, setFilteredAssignments] = useState(assignments);
    const [searchResults, setSearchResults] = useState([]);

    const [showStickyToolbar, setShowStickyToolbar] = useState(false);
    const [navbarHeight, setNavbarHeight] = useState(0);
    const columnToggleRef = useRef(null);

    const { companySlug } = useParams();
    const location = useLocation();
    const { userProfile } = useAuth();

    const enabledExtraQuoteTypes = React.useMemo(() => {
        return getEnabledExtraQuoteTypes(company);
    }, [company?.settings]);

    const quoteTypeFilterOptions = React.useMemo(() => {
        const base = [
            { key: "roof", label: "Roof", Icon: FiHome },
            { key: "wall", label: "Wall", Icon: BrickWall },
            { key: "floor", label: "Floor", Icon: Grid2X2 },
            { key: "timber", label: "Timber", Icon: GiWoodBeam },
            { key: "estimation", label: "Estimation", Icon: FiDollarSign },
        ];

        const extras = enabledExtraQuoteTypes.map(type => ({
            key: type,
            label: getQuoteLabel(type),
            Icon: type === "beams" ? GiWoodPile : SquareStack,
        }));

        return [...base, ...extras];
    }, [enabledExtraQuoteTypes]);

    const expandedAccordionsRef = useRef({});
    const injectedJobsRef = useRef({});
    // Tracks job IDs that matched the current search via a child_unique_id field,
    // so the expand effect can auto-open their accordions.
    const childSearchMatchIdsRef = useRef(new Set());

    const [editOpen, setEditOpen] = useState(false);
    const [editJobId, setEditJobId] = useState(null);

    // Virtualization with TanStack
    const parentRef = useRef(null);
    const tableContainerRef = useRef(null);
    const tableBodyRef = useRef(null);

    const [accordionStates, setAccordionStates] = useState({});

    // Prevents the filteredAssignments→expandedAssignments pipeline from re-running
    // when patchJob does a targeted in-place update (bypassing the full expand pipeline).
    const skipFilterPipelineRef = useRef(false);

    const rowVirtualizer = useVirtualizer({
        count: expandedAssignments.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => 56, // Average row height in pixels - default 56
        overscan: 40, // Render 5 extra rows above/below viewport for smooth scrolling - 40 seems fine
    });

    // Accordion design for Parent Jobs
    const toggleAccordion = (jobId) => {
        const isNowOpen = !expandedAccordionsRef.current[jobId];
        expandedAccordionsRef.current[jobId] = isNowOpen;

        setAccordionStates(prev => ({ ...prev, [jobId]: isNowOpen }))

        if (isNowOpen && !injectedJobsRef.current[jobId]) {
            // First time opening - inject child rows into data
            injectedJobsRef.current[jobId] = true;
            setExpandedAssignments(prev => {
                const parentIndex = prev.findIndex(a =>
                    (a._originalId === jobId || a._id === jobId || a.id === jobId) && a._isAccordionParent
                );
                if (parentIndex === -1) return prev;

                const parent = prev[parentIndex];
                if (!parent._childQuotes?.length) return prev;


                const children = parent._childQuotes.map(child => ({
                    ...parent,
                    displayUniqueId: child.displayUniqueId,
                    _displayId: `${jobId}_${child.type}_${child.id}`,
                    _originalId: jobId,
                    _quoteType: child.type,
                    _quoteData: child.data,
                    _isCombined: false,
                    _isAccordionChild: true,
                    _isAccordionParent: false,
                    _childLabel: child.label,
                    _childQuotes: undefined,
                    _childCount: undefined,
                }));

                const next = [...prev];
                next.splice(parentIndex + 1, 0, ...children);
                return next;
            });
        } else {
            // Toggle visibility - remove or re-add children
            setExpandedAssignments(prev => {
                if (isNowOpen) {
                    // Re-inject children
                    const parentIndex = prev.findIndex(a =>
                        (a._originalId === jobId || a._id === jobId || a.id === jobId) && a._isAccordionParent
                    );
                    if (parentIndex === -1) return prev;

                    const parent = prev[parentIndex];
                    if (!parent._childQuotes?.length) return prev;

                    const children = parent._childQuotes.map(child => ({
                        ...parent,
                        displayUniqueId: child.displayUniqueId,
                        _displayId: `${jobId}_${child.type}_${child.id}`,
                        _originalId: jobId,
                        _quoteType: child.type,
                        _quoteData: child.data,
                        _isCombined: false,
                        _isAccordionChild: true,
                        _isAccordionParent: false,
                        _childLabel: child.label,
                        _childQuotes: undefined,
                        _childCount: undefined,
                    }));

                    const next = [...prev];
                    next.splice(parentIndex + 1, 0, ...children);
                    return next;
                } else {
                    // Remove children from data
                    return prev.filter(a =>
                        !(a._isAccordionChild && a._originalId === jobId)
                    );
                }
            });
        }
    };


    const useDebounce = (value, delay) => {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            return () => clearTimeout(handler);
        }, [value, delay]);

        return debouncedValue;
    };

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    const STORAGE_KEYS = {
        columnVisibility: 'jobEditor_columnVisibility',
        columnSizing: 'jobEditor_columnSizing',
        quoteSentFilter: 'jobEditor_quoteSentFilter',
        urgentFilter: 'jobEditor_urgentFilter',
        hideCompleted: 'jobEditor_hideCompleted',
        jobTypeFilter: 'jobEditor_jobTypeFilter',
        sortBy: 'editor_sortBy',
        searchQuery: 'jobEditor_searchQuery',
    };

    const saveToStorage = (key, value) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.warn('Failed to save to localStorage:', error);
        }
    };

    const loadFromStorage = (key, defaultValue) => {
        try {
            const stored = localStorage.getItem(key);
            return stored ? JSON.parse(stored) : defaultValue;
        } catch (error) {
            console.warn('Failed to load from localStorage:', error);
            return defaultValue;
        }
    };

    const [columnVisibility, setColumnVisibility] = useState(() =>
        loadFromStorage(STORAGE_KEYS.columnVisibility, {
            uniqueId: true,
            customerName: true,
            jobType: true,
            mainStatus: true,
            quotes: true,
            assignee: true,
            priority: true,
            createdAt: true,
            invoiced: true,
            etaDate: true,
            note: true,
        })
    );

    const [notesOverrides, setNotesOverrides] = useState({});

    const [columnSizing, setColumnSizing] = useState(() =>
        loadFromStorage(STORAGE_KEYS.columnSizing, {})
    );

    // selectedQuoteTypes and invoiceFilter are props from parent (EditorSchedule) and are
    // applied server-side. shouldShowAssignment still checks them for search results and
    // realtime upserts, which don't go through the filtered RPC page.
    const shouldShowAssignment = (assignment) => {
        const hasRoof = !!getAggregateQuoteByType(assignment.quotes, 'roof');
        const hasWall = !!getAggregateQuoteByType(assignment.quotes, 'wall');
        const hasFloor = !!getAggregateQuoteByType(assignment.quotes, 'floor');
        const hasBeams = !!getAggregateQuoteByType(assignment.quotes, "beams");
        const hasFlooringSheets = !!getAggregateQuoteByType(assignment.quotes, "flooring_sheets");
        const jobType = assignment.jobType || assignment.job_type;
        const hasTimber = !!getAggregateQuoteByType(assignment.quotes, 'timber') ||
            jobType === 'Timber' ||
            assignment.selected_departments?.includes('timber');
        const hasEstimation = jobType === 'Estimation' || jobType === 'Design & Estimation';

        const matchesQuoteTypeFilter = (
            (hasRoof && selectedQuoteTypes.roof) ||
            (hasWall && selectedQuoteTypes.wall) ||
            (hasFloor && selectedQuoteTypes.floor) ||
            (hasTimber && selectedQuoteTypes.timber) ||
            (hasBeams && selectedQuoteTypes.beams) ||
            (hasFlooringSheets && selectedQuoteTypes.flooring_sheets) ||
            (hasEstimation && selectedQuoteTypes.estimation)
        );

        // Invoice filter logic
        let matchesInvoiceFilter = true;
        if (invoiceFilter !== 'all') {
            const quotes = assignment.quotes || {};
            const invoiceStatuses = [];

            Object.values(quotes || {}).forEach(q => {
                if (q?.isRequired || q?.is_required) {
                    invoiceStatuses.push(isQuoteInvoiceSent(q, invoiceCompany));
                }
            });



            const allSent = invoiceStatuses.every(Boolean);
            const someSent = invoiceStatuses.some(Boolean);
            const noneSent = invoiceStatuses.every(status => !status);

            if (invoiceStatuses.length === 0) {
                matchesInvoiceFilter = false;
            } else if (invoiceFilter === 'all_sent') {
                matchesInvoiceFilter = allSent;
            } else if (invoiceFilter === 'partial') {
                matchesInvoiceFilter = someSent && !allSent;
            } else if (invoiceFilter === 'not_sent') {
                matchesInvoiceFilter = noneSent;
            }
        }

        // quoteSentFilter, urgentFilter, hideEstimationCompleted and jobTypeFilter are handled at database level

        return matchesQuoteTypeFilter && matchesInvoiceFilter;
    };

    // quoteSentFilter and urgentFilter are now props from parent (EditorSchedule)

    // hideEstimationCompleted is now a prop from parent (EditorSchedule)

    // Directly patches a single job's rows in both filteredAssignments and expandedAssignments
    // WITHOUT triggering the full filter→expand pipeline.
    // Called by EditorSchedule for subscription UPDATE events so the parent never re-renders.
    const patchJob = useCallback((jobId, patcher) => {
        // Block the useEffect that rebuilds expandedAssignments from filteredAssignments,
        // since we're patching expandedAssignments ourselves in the same batch.
        skipFilterPipelineRef.current = true;

        setFilteredAssignments(prev => prev.map(row =>
            (row._id === jobId || row.id === jobId) ? patcher(row) : row
        ));

        setExpandedAssignments(prev => prev.map(row => {
            const matchesJob = row._id === jobId || row._originalId === jobId || row.id === jobId;
            if (!matchesJob) return row;
            const patched = patcher(row);

            // For accordion child rows: quotes are UUID-keyed, so find by quote_type field
            // rather than by string key. Also refresh _quoteData from the updated entry.
            if (row._isAccordionChild && row._quoteType) {
                const freshQuoteEntry = Object.values(patched.quotes || {}).find(
                    q => q && (q.quote_type === row._quoteType || q.quoteType === row._quoteType)
                ) || patched.quotes?.[row._quoteType] || row._quoteData;
                return { ...patched, _quoteData: freshQuoteEntry };
            }

            // For accordion parent rows: rebuild _childQuotes so that when the accordion
            // is expanded (or re-expanded) after a subscription update, child rows show
            // fresh data without needing a full filter pipeline run.
            if (row._isAccordionParent) {
                const parentDisplayId = patched.uniqueId || patched.unique_id || null;
                const childQuotes = Object.entries(patched.quotes || {})
                    .filter(([, q]) => q?.isRequired || q?.is_required)
                    .map(([key, q]) => ({
                        id: key,
                        type: q.quote_type || q.quoteType,
                        label: q.quote_type || q.quoteType,
                        data: q,
                        displayUniqueId: q.child_unique_id || q.child_uniqueId || parentDisplayId,
                    }));
                return { ...patched, _childQuotes: childQuotes };
            }

            return patched;
        }));

        // Reset the guard after React has committed this render + run effects
        setTimeout(() => { skipFilterPipelineRef.current = false; }, 0);
    }, []);

    const getJobId = (job) => job?._id || job?.id;

    // Must match the ORDER BY in the get_editor_table_job_ids RPC, otherwise
    // realtime upserts land in a different position than a fresh fetch would put them.
    const compareJobs = useCallback((a, b) => {
        const aDate = new Date(a.createdAt || a.created_at || 0).getTime();
        const bDate = new Date(b.createdAt || b.created_at || 0).getTime();

        if (sortBy === 'alphabetical' || sortBy === 'reverse_alphabetical') {
            const aName = a.customerName || a.customer_name || '';
            const bName = b.customerName || b.customer_name || '';
            const byName = sortBy === 'alphabetical' ? aName.localeCompare(bName) : bName.localeCompare(aName);
            return byName || bDate - aDate;
        }

        if (sortBy === 'priority') {
            const aUrgentQuote = a.jobInformation?.urgentQuote ? 1 : 0;
            const bUrgentQuote = b.jobInformation?.urgentQuote ? 1 : 0;
            if (aUrgentQuote !== bUrgentQuote) return bUrgentQuote - aUrgentQuote;

            const aUrgentDesign = a.jobInformation?.urgentDesign ? 1 : 0;
            const bUrgentDesign = b.jobInformation?.urgentDesign ? 1 : 0;
            if (aUrgentDesign !== bUrgentDesign) return bUrgentDesign - aUrgentDesign;

            return aDate - bDate;
        }

        if (sortBy === 'oldest') return aDate - bDate;

        return bDate - aDate; // newest default
    }, [sortBy]);

    const upsertJob = useCallback((job) => {
        const jobId = getJobId(job);
        if (!jobId) return;

        setFilteredAssignments(prev => {
            const withoutOld = prev.filter(item => getJobId(item) !== jobId);

            if (!shouldShowAssignment(job)) {
                return withoutOld;
            }

            return [...withoutOld, job].sort(compareJobs);
        });
    }, [compareJobs, shouldShowAssignment]);

    const removeJob = useCallback((jobId) => {
        setFilteredAssignments(prev =>
            prev.filter(item => getJobId(item) !== jobId)
        );

        setExpandedAssignments(prev =>
            prev.filter(item =>
                item._id !== jobId &&
                item.id !== jobId &&
                item._originalId !== jobId
            )
        );
    }, []);

    useImperativeHandle(ref, () => ({
        patchJob,
        upsertJob,
        removeJob,
    }), [patchJob, upsertJob, removeJob]);

    const expandAssignmentsByQuoteType = (assignments) => {
        const expanded = [];

        assignments.forEach(assignment => {
            const jobId = assignment._id || assignment.id;
            const jobType = assignment.jobType || assignment.job_type;
            const includesDesign = jobType === 'Design' || jobType === 'Design & Estimation';

            // 🔥 Normalize parent ID once
            const parentDisplayId =
                assignment.uniqueId ||
                assignment.unique_id ||
                assignment.child_unique_id ||
                null;

            if (viewMode === 'combined') {
                if (includesDesign) {
                    const childQuotes = [];
                    // if (assignment.quotes?.roof?.isRequired) {
                    //     childQuotes.push({ type: 'roof', label: 'Roof', data: assignment.quotes.roof });
                    // }
                    // if (assignment.quotes?.wall?.isRequired) {
                    //     childQuotes.push({ type: 'wall', label: 'Wall', data: assignment.quotes.wall });
                    // }
                    // if (assignment.quotes?.floor?.isRequired) {
                    //     childQuotes.push({ type: 'floor', label: 'Floor', data: assignment.quotes.floor });
                    // }
                    // if (assignment.quotes?.timber?.isRequired) {
                    //     childQuotes.push({ type: 'timber', label: 'Timber', data: assignment.quotes.timber });
                    // }

                    Object.entries(assignment.quotes || {}).forEach(([key, quote]) => {
                        if (quote?.isRequired) {
                            childQuotes.push({
                                id: key,
                                type: quote.quote_type,
                                label: quote.quote_type,
                                data: quote,

                                // 🔥 Normalize child ID here
                                displayUniqueId:
                                    quote.child_unique_id ||
                                    quote.child_uniqueId ||
                                    parentDisplayId
                            });
                        }
                    });
                    // let assignmentQuotes = Object.entries(assignment.quotes)

                    // console.log(assignmentQuotes)

                    // assignmentQuotes.forEach(quote => {

                    //     if (!quote[1].isRequired) {
                    //         childQuotes.push({ type: quote[1].type, uniqueId: quote[0], label: `${quote[1].type}`, data: quote[1] })
                    //     }
                    // });

                    // Parent row only — children injected lazily on first toggle
                    expanded.push({
                        ...assignment,
                        displayUniqueId: parentDisplayId,
                        _displayId: jobId,
                        _originalId: jobId,
                        _quoteType: null,
                        _isCombined: true,
                        _isAccordionParent: childQuotes.length > 0,
                        _childQuotes: childQuotes,
                        _childCount: childQuotes.length,
                    });
                } else {
                    expanded.push({
                        ...assignment,
                        displayUniqueId: parentDisplayId,
                        _displayId: jobId,
                        _originalId: jobId,
                        _quoteType: null,
                        _isCombined: true,
                        _isAccordionParent: false,
                        _childQuotes: [],
                        _childCount: 0,
                    });
                }
            } else {
                if (includesDesign) {
                    const childQuotes = [];
                    // if (assignment.quotes?.roof?.isRequired && selectedQuoteTypes.roof) {
                    //     childQuotes.push({ type: 'roof', label: 'Roof', data: assignment.quotes.roof });
                    // }
                    // if (assignment.quotes?.wall?.isRequired && selectedQuoteTypes.wall) {
                    //     childQuotes.push({ type: 'wall', label: 'Wall', data: assignment.quotes.wall });
                    // }
                    // if (assignment.quotes?.floor?.isRequired && selectedQuoteTypes.floor) {
                    //     childQuotes.push({ type: 'floor', label: 'Floor', data: assignment.quotes.floor });
                    // }
                    // if (assignment.quotes?.timber?.isRequired && selectedQuoteTypes.timber) {
                    //     childQuotes.push({ type: 'timber', label: 'Timber', data: assignment.quotes.timber });
                    // }

                    Object.entries(assignment.quotes || {}).forEach(([key, quote]) => {
                        if (quote?.isRequired) {
                            childQuotes.push({
                                id: key,
                                type: quote.quote_type,
                                label: quote.quote_type,
                                data: quote,

                                // 🔥 Normalize child ID here
                                displayUniqueId:
                                    quote.child_unique_id ||
                                    quote.child_uniqueId ||
                                    parentDisplayId
                            });
                        }
                    });

                    if (childQuotes.length > 0) {
                        // Parent row only — children injected lazily on first toggle
                        expanded.push({
                            ...assignment,
                            displayUniqueId: parentDisplayId,
                            _displayId: jobId,
                            _originalId: jobId,
                            _quoteType: null,
                            _isCombined: true,
                            _isAccordionParent: true,
                            _childQuotes: childQuotes,
                            _childCount: childQuotes.length,
                        });
                    } else {
                        const needsEstimation = (jobType === 'Design & Estimation') && selectedQuoteTypes.estimation;
                        if (needsEstimation) {
                            expanded.push({
                                ...assignment,
                                displayUniqueId: parentDisplayId,
                                _displayId: jobId,
                                _originalId: jobId,
                                _quoteType: null,
                                _isCombined: true,
                                _isAccordionParent: false,
                                _childQuotes: [],
                                _childCount: 0,
                            });
                        }
                    }
                } else {
                    const needsEstimation = (jobType === 'Estimation' || jobType === 'Design & Estimation') && selectedQuoteTypes.estimation;
                    if (needsEstimation) {
                        expanded.push({
                            ...assignment,
                            displayUniqueId: parentDisplayId,
                            _displayId: jobId,
                            _originalId: jobId,
                            _quoteType: null,
                            _isCombined: true,
                            _isAccordionParent: false,
                            _childQuotes: [],
                            _childCount: 0,
                        });
                    }
                }
            }
        });

        return expanded;
    };


    const useInfiniteScroll = (callback, hasMore, isLoading, containerRef) => {
        const [isFetching, setIsFetching] = useState(false);

        useEffect(() => {
            if (!isFetching) return;
            fetchMoreData();
        }, [isFetching]);

        const fetchMoreData = async () => {
            await callback();
            setIsFetching(false);
        };

        useEffect(() => {
            const handleScroll = () => {
                const threshold = 600;

                // Bail out if already fetching, nothing more to load, or currently loading
                if (isFetching || !hasMore || isLoading) return;

                // Get the scroll container element
                const scrollContainer = containerRef?.current;
                if (!scrollContainer) return;

                // Calculate if scrolled near bottom of the container
                const scrolledToBottom =
                    scrollContainer.scrollTop + scrollContainer.clientHeight >=
                    scrollContainer.scrollHeight - threshold;

                if (!scrolledToBottom) return;

                setIsFetching(true);
            };

            const scrollContainer = containerRef?.current;
            if (!scrollContainer) return;

            scrollContainer.addEventListener('scroll', handleScroll);
            return () => scrollContainer.removeEventListener('scroll', handleScroll);
        }, [isFetching, hasMore, isLoading, containerRef]);

        return [isFetching, setIsFetching];
    };

    const [isFetching] = useInfiniteScroll(onLoadMore, hasMore, isLoadingMore, tableContainerRef);



    useEffect(() => {
        const detectNavbarHeight = () => {
            const navbar =
                document.querySelector('nav') ||
                document.querySelector('[role="navigation"]') ||
                document.querySelector('header') ||
                document.querySelector('.navbar') ||
                document.querySelector('#navbar');

            if (navbar) {
                const rect = navbar.getBoundingClientRect();
                setNavbarHeight(rect.width);
            } else {
                setNavbarHeight(0);
            }
        };

        const handleScroll = () => {
            if (columnToggleRef.current) {
                const rect = columnToggleRef.current.getBoundingClientRect();
                setShowStickyToolbar(rect.bottom < navbarHeight);
            }
        };

        detectNavbarHeight();
        window.addEventListener('resize', detectNavbarHeight);
        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            window.removeEventListener('resize', detectNavbarHeight);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [navbarHeight]);

    // Filter assignments based on search
    // Effect to perform search when search query changes
    useEffect(() => {
        if (!debouncedSearchQuery.trim()) {
            setSearchResults([]);
            return;
        }

        const performSearch = async () => {
            setIsTableLoading(true);

            try {
                const filters = {
                    searchQuery: debouncedSearchQuery,
                    ...(showOnlyMine && { showOnlyMine: true }),
                    ...(selectedUser && { userId: selectedUser })
                };

                const serverResults = await searchJobs(companyId, filters);

                const loadedIds = new Set(assignments.map(a => a._id || a.id));
                const newResults = serverResults.filter(a => !loadedIds.has(a._id || a.id));

                // Store server results so they persist across filter changes
                setSearchResults(newResults);
            } catch (error) {
                console.error('Search failed:', error);
                setSearchResults([]);
            } finally {
                setIsTableLoading(false);
            }
        };

        performSearch();
    }, [debouncedSearchQuery, showOnlyMine, selectedUser, companyId, assignments]);

    // Effect to apply filters to assignments + search results
    useEffect(() => {
        // Combine loaded assignments with search results
        const allAssignments = [...assignments, ...searchResults];

        if (!debouncedSearchQuery.trim()) {
            // No search query — clear child-id matches and apply filters only
            childSearchMatchIdsRef.current = new Set();
            const filtered = allAssignments.filter(assignment => shouldShowAssignment(assignment));
            setFilteredAssignments(filtered);
        } else {
            // Has search query — filter by both search AND filters
            const query = debouncedSearchQuery.toLowerCase();
            const newMatchIds = new Set();

            const filtered = allAssignments.filter(assignment => {
                // Check if any child quote's display ID matches the query
                const matchesChildUniqueId = Object.values(assignment.quotes || {}).some(q =>
                    q?.child_unique_id?.toLowerCase().includes(query)
                );

                // Track jobs that matched via child_unique_id so the expand effect
                // can auto-open their accordions.
                if (matchesChildUniqueId) {
                    newMatchIds.add(assignment._id || assignment.id);
                }

                const matchesSearch = (
                    matchesChildUniqueId ||
                    assignment.uniqueId?.toLowerCase().includes(query) ||
                    assignment.unique_id?.toLowerCase().includes(query) ||
                    (assignment._id || assignment.id)?.toLowerCase().includes(query) ||
                    assignment.customerName?.toLowerCase().includes(query) ||
                    assignment.customer_name?.toLowerCase().includes(query) ||
                    assignment.customerAddress?.toLowerCase().includes(query) ||
                    assignment.customer_address?.toLowerCase().includes(query) ||
                    assignment.carpenterSupervisorName?.toLowerCase().includes(query) ||
                    assignment.carpenter_supervisor_name?.toLowerCase().includes(query) ||
                    assignment.jobType?.toLowerCase().includes(query) ||
                    assignment.job_type?.toLowerCase().includes(query) ||
                    assignment.mainStatus?.toLowerCase().includes(query) ||
                    assignment.main_status?.toLowerCase().includes(query) ||
                    assignment.estimator?.name?.toLowerCase().includes(query) ||
                    Object.values(assignment.quotes || {}).some(q =>
                        q?.designers?.some(d => d.name?.toLowerCase().includes(query))
                    )
                );
                return matchesSearch && shouldShowAssignment(assignment);
            });

            childSearchMatchIdsRef.current = newMatchIds;
            setFilteredAssignments(filtered);
        }
    }, [debouncedSearchQuery, assignments, searchResults, selectedQuoteTypes, invoiceFilter]);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.columnVisibility, columnVisibility);
    }, [columnVisibility]);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.columnSizing, columnSizing);
    }, [columnSizing]);

    // Save filter states to localStorage when they change
    useEffect(() => {
        saveToStorage(STORAGE_KEYS.quoteSentFilter, quoteSentFilter);
    }, [quoteSentFilter]);

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.urgentFilter, urgentFilter);
    }, [urgentFilter]);

    // jobTypeFilter is now managed by parent component, no need to save here

    useEffect(() => {
        saveToStorage(STORAGE_KEYS.searchQuery, searchQuery);
    }, [searchQuery]);

    useEffect(() => {
        // patchJob sets this flag to avoid the full re-expand on targeted row updates.
        if (skipFilterPipelineRef.current) return;
        const expanded = expandAssignmentsByQuoteType(filteredAssignments);
        // Reset injection tracking — data is rebuilt from scratch
        injectedJobsRef.current = {};
        expandedAccordionsRef.current = {};

        const matchIds = childSearchMatchIdsRef.current;
        const activeQuery = debouncedSearchQuery?.toLowerCase().trim() || '';

        if (matchIds.size > 0) {
            // Auto-open parent rows whose match came from a child_unique_id search.
            // We inject their children inline here (same logic as toggleAccordion's
            // first-open branch) so the child rows are immediately visible.
            const result = [...expanded];
            const newAccordionStates = {};

            matchIds.forEach(jobId => {
                const parentIdx = result.findIndex(a =>
                    (a._originalId === jobId || a._id === jobId || a.id === jobId) &&
                    a._isAccordionParent
                );
                if (parentIdx === -1) return;

                const parent = result[parentIdx];
                if (!parent._childQuotes?.length) return;

                // Mark as open in both ref and state
                expandedAccordionsRef.current[jobId] = true;
                injectedJobsRef.current[jobId] = true;
                newAccordionStates[jobId] = true;

                // Only inject children that actually match the search query.
                // If a search matched via the parent's own ID/name, inject all children.
                // If the match was specifically via child_unique_id, only inject matching children.
                const parentMatchesDirectly = (
                    parent.uniqueId?.toLowerCase().includes(activeQuery) ||
                    parent.unique_id?.toLowerCase().includes(activeQuery) ||
                    parent.customerName?.toLowerCase().includes(activeQuery) ||
                    parent.customer_name?.toLowerCase().includes(activeQuery)
                );

                const childrenToInject = parentMatchesDirectly
                    ? parent._childQuotes
                    : parent._childQuotes.filter(child =>
                        child.displayUniqueId?.toLowerCase().includes(activeQuery) ||
                        child.type?.toLowerCase().includes(activeQuery) ||
                        child.data?.designStatus?.toLowerCase().includes(activeQuery) ||
                        child.data?.design_status?.toLowerCase().includes(activeQuery)
                    );

                const children = (childrenToInject.length > 0 ? childrenToInject : parent._childQuotes).map(child => ({
                    ...parent,
                    displayUniqueId: child.displayUniqueId,
                    _displayId: `${jobId}_${child.type}_${child.id}`,
                    _originalId: jobId,
                    _quoteType: child.type,
                    _quoteData: child.data,
                    _isCombined: false,
                    _isAccordionChild: true,
                    _isAccordionParent: false,
                    _childLabel: child.label,
                    _childQuotes: undefined,
                    _childCount: undefined,
                }));
                result.splice(parentIdx + 1, 0, ...children);
            });

            setAccordionStates(newAccordionStates);
            setExpandedAssignments(result);
        } else {
            setAccordionStates({});
            setExpandedAssignments(expanded);
        }
    }, [filteredAssignments, viewMode, selectedQuoteTypes, debouncedSearchQuery]);

    const closeAllTooltips = useCallback(() => {
        window.dispatchEvent(new Event('job-editor-close-tooltips'));
    }, []);

    useEffect(() => {
        const scrollEl = tableContainerRef.current;
        if (!scrollEl) return;

        scrollEl.addEventListener('scroll', closeAllTooltips, { passive: true });
        window.addEventListener('blur', closeAllTooltips);
        document.addEventListener('mouseleave', closeAllTooltips);

        return () => {
            scrollEl.removeEventListener('scroll', closeAllTooltips);
            window.removeEventListener('blur', closeAllTooltips);
            document.removeEventListener('mouseleave', closeAllTooltips);
        };
    }, [closeAllTooltips]);


    const handleOpenAssignment = (assignmentId) => {
        window.open(`/${companySlug}/assignment/${assignmentId}`, '_blank');
    };

    const handleEditAssignment = (assignmentId) => {
        setEditJobId(assignmentId)
        setEditOpen(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-AU', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        });
    };

    const getPriorityIndicator = (assignment) => {
        const isUrgent = assignment.jobInformation?.urgentQuote || assignment.jobInformation?.urgentDesign ||
            assignment.job_information?.urgent_quote || assignment.urgent_quote;
        if (isUrgent) return { color: 'bg-red-500', label: 'Urgent' };
        return { color: 'bg-gray-300', label: 'Normal' };
    };

    const handleAssignDesigner = async (assignmentId, quoteType, designerId, designerName, assignment) => {
        try {
            await assignDesignerToJob(assignmentId, quoteType, designerId);

            const notificationData = {
                user_id: designerId,
                company_id: company.id,
                type: NOTIFICATION_TYPES.ASSIGNMENT_ASSIGNED,
                title: "New Design Assignment",
                message: `Assignment ${assignment.unique_id} (${quoteType}) assigned to you`,
                priority: "normal",
                related_type: "assignment",
                related_id: assignment.id,
                action_url: `${companySlug}/assignment/${assignment.id}`
            }

            await createNotification(notificationData)


        } catch (error) {
            console.error('Failed to assign designer:', error);
        }
    };

    // const handleForceFinish = async (assignmentId, job, type, quoteType = null) => {
    //     console.log(job);
    //     return;
    //     try {
    //         let result;
    //         result = await forceFinish(assignmentId, userProfile, type, quoteType);

    //         if (result?.success) {
    //             Toast.success(result.message || 'Force finish completed successfully');
    //             // Refresh the page to show updated data
    //             window.location.reload();
    //         } else {
    //             Toast.error(result?.error || 'Failed to force finish');
    //         }
    //     } catch (error) {
    //         console.error('Error in handleForceFinish:', error);
    //         Toast.error('Failed to force finish');
    //     }
    // };

    const handleForceFinish = async (assignmentId, maybeAssignmentOrType, maybeType = null, quoteType = null) => {
        try {
            let assignment = null;
            let type = maybeType;

            // delivery call shape:
            // handleForceFinish(jobId, assignment, 'delivery')
            if (typeof maybeAssignmentOrType === 'object' && maybeAssignmentOrType !== null) {
                assignment = maybeAssignmentOrType;
            } else {
                // design / estimation / all call shape:
                // handleForceFinish(jobId, 'design', 'roof')
                type = maybeAssignmentOrType;
            }


            const result = await forceFinish(
                assignmentId,
                userProfile,
                type,
                quoteType,
                assignment
            );

            if (result?.success) {
                Toast.success(result.message || 'Force finish completed successfully');
                window.location.reload();
            } else {
                Toast.error(result?.error || 'Failed to force finish');
            }
        } catch (error) {
            console.error('Error in handleForceFinish:', error);
            Toast.error('Failed to force finish');
        }
    };

    const handleToggleHold = async (assignmentId, currentStatus) => {
        try {
            let result;
            result = await toggleHold(assignmentId, currentStatus, userProfile);

            if (result?.success) {
                Toast.success(result.message || 'Job completed successfully');
                // Refresh the page to show updated data
                // window.location.reload();
            } else {
                Toast.error(result?.error || 'Failed to pause/resume job.');
            }
        } catch (error) {
            console.error('Error in handleToggleHold:', error);
            Toast.error('Failed to pause/resume job');
        }
    };

    const handleToggleSentProduction = async (assignmentId, quoteType, currentStatus, quoteId = null) => {
        try {
            await toggleSentProduction(assignmentId, quoteType, currentStatus, userProfile, quoteId);
            Toast.success(currentStatus ? 'Removed from Production' : 'Sent to Production');
        } catch (error) {
            console.error('Error toggling sent_production:', error);
            Toast.error('Failed to update Production status');
        }
    };

    const handleToggleQuoteHold = async (assignmentId, quoteType, currentStatus) => {
        try {
            const result = await toggleQuoteHold(assignmentId, quoteType, currentStatus, userProfile);

            if (result?.success) {
                Toast.success(result.message || 'Quote updated successfully');
            } else {
                Toast.error(result?.error || 'Failed to pause/resume quote.');
            }
        } catch (error) {
            console.error('Error in handleToggleQuoteHold:', error);
            Toast.error('Failed to pause/resume quote');
        }
    };

    const AddressTooltip = ({ address }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, showAbove: false });
        const [triggerRef, setTriggerRef] = useState(null);

        useEffect(() => {
            const close = () => setIsHovered(false);
            window.addEventListener('job-editor-close-tooltips', close);
            return () => window.removeEventListener('job-editor-close-tooltips', close);
        }, []);

        // Calculate tooltip position with viewport awareness
        useEffect(() => {
            if (isHovered && triggerRef) {
                const rect = triggerRef.getBoundingClientRect();
                const tooltipHeight = 100; // Taller for combined view
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - rect.bottom;
                const spaceAbove = rect.top;

                const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;

                setTooltipPosition({
                    x: rect.left,
                    y: showAbove ? rect.top - 4 : rect.bottom + 4,
                    showAbove
                });
            }
        }, [isHovered, triggerRef]);

        return (
            <>
                <div
                    ref={setTriggerRef}
                    className={`${address && "cursor-help"}`}
                    onPointerEnter={() => setIsHovered(true)}
                    onPointerLeave={() => setIsHovered(false)}
                    onPointerCancel={() => setIsHovered(false)}
                >
                    {address || "No address set"}
                </div>

                {isHovered && address && createPortal(
                    <div
                        className="fixed z-[9999] pointer-events-none bg-zinc-100 border border-zinc-400 text-black text-sm px-2 py-1 rounded shadow-lg"
                        style={{
                            left: tooltipPosition.x,
                            top: tooltipPosition.showAbove
                                ? tooltipPosition.y
                                : tooltipPosition.y,
                        }}
                    >
                        {address || "No address set for this job"}
                    </div>,
                    document.body
                )}
            </>
        );
    };

    const columns = React.useMemo(() => [
        {
            id: 'uniqueId',
            accessorKey: 'uniqueId',
            header: () => (
                <div className="flex items-center">
                    <FiHash className="w-3 h-3 mr-2 text-zinc-100" />
                    Job ID
                </div>
            ),
            cell: ({ row }) => {
                const isParent = row.original._isAccordionParent;
                const isChild = row.original._isAccordionChild;
                const jobId = row.original._originalId || row.original._id || row.original.id;

                const isOpen = accordionStates[jobId] || false;

                return (
                    <div className="min-w-0 flex-1">
                        <div className={`flex items-center gap-1.5 ${isChild ? 'pl-6' : ''}`}>


                            {/* Child indent */}
                            {isChild && (
                                <span className="flex-shrink-0 text-gray-300 ml-1">└</span>
                            )}

                            {/* Job ID - fixed width to ensure consistent alignment */}
                            <div className="flex items-center min-w-0 flex-1 w-full justify-between">
                                <span className='flex flex-row text-base items-center font-semibold text-gray-900 group-hover:text-blue-700 transition-colors min-w-0'>
                                    <Hash className='w-4 h-4 flex-shrink-0 mr-1 select-none opacity-60' />
                                    <span className="truncate">
                                        {row.original.displayUniqueId}
                                    </span>
                                </span>

                                {/* Indicators - always aligned */}
                                <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                                    {/* [Parent] indicator */}
                                    {isParent && (
                                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                                            Parent
                                        </span>
                                    )}

                                    {/* Parts count for parent rows */}
                                    {isParent && row.original._childCount > 0 && (
                                        <span className="text-xs font-medium text-gray-500 whitespace-nowrap w-10">
                                            {row.original._childCount} {row.original._childCount === 1 ? 'Part' : 'Parts'}
                                        </span>
                                    )}

                                    {/* Child quote type label */}
                                    {isChild && (
                                        <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
                                            ({row.original._childLabel})
                                        </span>
                                    )}

                                    {/* Accordion toggle button for parent rows */}
                                    {isParent && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleAccordion(jobId);
                                            }}
                                            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-md hover:bg-gray-200 active:bg-gray-300"
                                            title="Toggle parts"
                                        >
                                            <FiChevronRight
                                                id={`accordion-chevron-${jobId}`}
                                                className={`w-5 h-5 text-gray-700 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-90' : 'rotate-0'}`}
                                            />
                                        </button>
                                    )}
                                </div>


                            </div>
                        </div>
                    </div>
                );
            },
            size: 260,
            minSize: 160,
            maxSize: 360,
        },
        {
            id: 'customerName',
            accessorKey: 'customerName',
            header: () => (
                <div className="flex items-center">
                    <FiUser className="w-3 h-3 mr-1 text-zinc-100" />
                    Customer
                </div>
            ),
            cell: ({ row }) => (
                <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-800 truncate">
                        {row.original.customerName || row.original.customer_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                        <AddressTooltip
                            address={row.original?.customerAddress}
                        />
                    </div>
                </div>
            ),
            size: 200,
            minSize: 150,
            maxSize: 300,
        },
        {
            id: 'jobType',
            accessorKey: 'jobType',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiTool className="w-3 h-3 mr-1 text-zinc-100" />
                    Type
                </div>
            ),
            cell: ({ row }) => {
                const jobType = row.original.jobType || row.original.job_type;
                return (
                    <div className="text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium shadow-sm ${jobType === 'Design' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                            jobType === 'Estimation' ? 'bg-purple-100 text-purple-800 border border-purple-200' :
                                jobType === 'Timber' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
                                    'bg-emerald-100 text-emerald-800 border border-emerald-200'
                            }`}>
                            {jobType === 'Design' ? 'D' : jobType === 'Estimation' ? 'E' : jobType === 'Timber' ? 'T' : 'D&E'}
                        </span>
                    </div>
                );
            },
            size: 100,
            minSize: 80,
            maxSize: 120,
        },
        {
            id: 'mainStatus',
            accessorKey: 'mainStatus',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiClock className="w-3 h-3 mr-1 text-zinc-100" />
                    Status
                </div>
            ),
            cell: ({ row }) => {
                const status = row.original.mainStatus || row.original.main_status;
                const isTimber = !!getAggregateQuoteByType(row.original.quotes, 'timber');

                // Use _isCombined (same signal as the Departments column) rather than
                // _isAccordionChild so that per-type rows in separated view — which may
                // not have _isAccordionChild set — also get per-type status rendering.
                const isChild = (!row.original?._isCombined && !!row.original?._quoteType);


                const displayStatus = isTimber
                    ? (status === 'ready_for_delivery' ? 'ready for delivery' :
                        status === 'completed' ? 'Completed' : status?.replace('_', ' '))
                    : status?.replace('_', ' ');

                const getStatusDot = (status) => {
                    switch (status) {
                        case 'completed': return 'bg-green-500';
                        case 'in_progress': return 'bg-yellow-500';
                        case 'in_design': return 'bg-yellow-500';
                        case 'in_estimation': return 'bg-yellow-500';
                        case 'waiting_assignment': return 'bg-red-600/80';
                        case 'waiting_design': return 'bg-red-600/80';
                        default: return 'bg-gray-400';
                    }
                };

                if (row.original?.is_cancelled || row.original?.isCancelled) {
                    return (
                        <div className="flex text-center items-center justify-center">
                            <div className="flex flex-col gap-1 text-xs font-semibold w-full">
                                <p className="py-1 px-2 w-full rounded text-white bg-red-600 text-center">
                                    Cancelled
                                </p>
                            </div>
                        </div>
                    );
                }

                if (!isChild && row.original?.on_hold) return (
                    <div className='flex flex-col gap-1 text-xs font-semibold w-full'>
                        <p className={`py-1 px-2 w-full rounded text-white bg-gray-500 text-center`}>On Hold</p>
                    </div>
                )

                const renderChildStatus = (quoteType) => {
                    // Read directly from _quoteData for this specific child quote,
                    // falling back to the type aggregate — mirrors how the assignee column
                    // reads _quoteData?.designers instead of going through a helper.
                    const quote = row.original?._quoteData || getAggregateQuoteByType(row.original?.quotes, quoteType) || null;

                    if (quote?.on_hold) {
                        return (
                            <div className='flex flex-col gap-1 text-xs font-semibold w-full'>
                                <p className={`py-1 px-2 w-full rounded text-white bg-gray-500 text-center`}>On Hold</p>
                            </div>
                        );
                    }

                    // Read raw status key directly (e.g. "in_progress", "waiting_assignment")
                    // getStatusDot accepts raw DB status keys — no text-conversion round-trip needed.
                    const rawStatus = quote?.designStatus || quote?.design_status;
                    const statusText = getDesignStatusText(rawStatus);

                    return (
                        <p className={`py-1 px-2 w-full rounded text-white ${getStatusDot(rawStatus || '')}`}>
                            {statusText}
                        </p>
                    );
                }

                const renderMainStatus = () => {
                    const jt = row.original?.job_type || row.original?.jobType;

                    if (jt === "Timber") {
                        const timberStatus = row.original?.main_status || row.original?.mainStatus || row.original?.current_status;
                        const timberStatusColors = {
                            ready_for_delivery: 'bg-teal-500',
                            delivery_scheduled: 'bg-blue-500',
                            in_delivery: 'bg-yellow-500',
                            completed: 'bg-green-500',
                        };
                        const timberStatusLabels = {
                            ready_for_delivery: 'Ready for Delivery',
                            delivery_scheduled: 'Delivery Scheduled',
                            in_delivery: 'In Delivery',
                            completed: 'Completed',
                        };
                        const color = timberStatusColors[timberStatus] || 'bg-gray-400';
                        const label = timberStatusLabels[timberStatus] || (timberStatus?.replace(/_/g, ' ') || '—');
                        return <p className={`py-1 px-2 w-full rounded text-white ${color}`}>{label}</p>;
                    }

                    if (jt === "Design") {
                        return (
                            <p className={`py-1 px-2 w-full rounded text-white ${getStatusDot(getJobStatusSummary(row.original, "editor").toLowerCase().replaceAll(" ", "_"))}`}>{getJobStatusSummary(row.original, "editor")}</p>
                        )
                    }

                    if (jt === "Estimation") {
                        if (row.original?.on_hold) {
                            return (
                                <div className='flex flex-col gap-1 text-xs font-semibold w-full'>
                                    <p className={`py-1 px-2 w-full rounded text-white bg-gray-500 text-center`}>On Hold</p>
                                </div>
                            )
                        } else {
                            return (
                                <p className={`py-1 px-2 w-full rounded text-white ${getStatusDot(getJobStatusSummary(row.original, "estimation").toLowerCase().replaceAll(" ", "_"))}`}>
                                    {getJobStatusSummary(row.original, 'estimation')}
                                </p>
                            )
                        }
                    }

                    if (jt === "Design & Estimation") {
                        return (
                            <p className={`py-1 px-2 w-full rounded text-white ${getStatusDot(getJobStatusSummary(row.original, "editor").toLowerCase().replaceAll(" ", "_"))}`}>{getJobStatusSummary(row.original, "editor")}</p>
                        )
                    }
                }

                return (
                    <div className="flex text-center items-center justify-center">
                        {/* <div className={`inline-flex items-center self-center justify-center px-2 py-1 rounded text-xs font-medium ${status === 'completed' ? 'bg-green-100 text-green-700' :
                            status === 'ready_for_delivery' && isTimber ? 'bg-teal-100 text-teal-700' :
                                status?.includes('in_') ? 'bg-blue-100 text-blue-700' :
                                    status?.includes('pending') ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-gray-100 text-gray-700'
                            }`}>
                           {getJobStatusSummary(row.original, "editor")}
                        </div> */}
                        <div className='flex flex-col gap-1 text-xs font-semibold w-full'>
                            {isChild ?
                                renderChildStatus(row?.original?._quoteType)
                                :
                                // <p className={`py-1 px-2 w-full rounded text-white ${getStatusDot(getJobStatusSummary(row.original, "editor").toLowerCase().replaceAll(" ", "_"))}`}>{getJobStatusSummary(row.original, "editor")}</p>
                                renderMainStatus()
                            }
                        </div>
                    </div>
                );
            },
            size: 150,
            minSize: 100,
            maxSize: 160,
        },
        {
            id: 'quotes',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiLayers className="w-3 h-3 mr-1 text-zinc-100" />
                    Departments
                </div>
            ),
            cell: ({ row }) => (
                <div className="flex items-center justify-center">
                    {row.original._isCombined ? (
                        <QuotesHoverTooltip assignment={row.original} />
                    ) : (
                        <SingleQuoteDisplay
                            assignment={row.original}
                            quoteType={row.original._quoteType}
                        />
                    )}
                </div>
            ),
            size: 160,
            minSize: 120,
            maxSize: 180,
        },
        {
            id: 'assignee',
            header: () => (
                <div className="flex items-center">
                    <FiUser className="w-3 h-3 mr-1 text-zinc-100" />
                    Assignee
                </div>
            ),
            cell: ({ row }) => {
                const assignment = row.original;
                const isChild = row.original._isAccordionChild;
                const childQuoteType = row.original._quoteType; // or whatever field identifies the quote type

                // Collect assignees
                const assignees = [];

                if (isChild && childQuoteType) {
                    // For child rows: use only _quoteData.designers — the designers assigned
                    // specifically to this quote (schedule.js now filters by child_job_id for
                    // new jobs, or by quote_type for old single-quote-per-type jobs).
                    // Do NOT fall back to the aggregate, which would show all designers of
                    // that type and make every child row look the same.
                    const designers = assignment._quoteData?.designers || [];


                    designers.forEach(designer => {
                        if (designer.userId && designer.name) {
                            assignees.push({
                                name: designer.name,
                                role: `${childQuoteType.charAt(0).toUpperCase() + childQuoteType.slice(1)} Designer`,
                                type: childQuoteType
                            });
                        }
                    });

                } else {
                    // For parent rows, show all assignees across all quote types

                    // Add estimator if exists
                    if (assignment.estimator?.user_id && assignment.estimator?.name) {
                        assignees.push({
                            name: assignment.estimator.name,
                            role: 'Estimator',
                            type: 'estimation'
                        });
                    }

                    // Add designers — from aggregate quotes (works for UUID-keyed and type-keyed)
                    Object.entries(assignment.quotes || {}).forEach(quote => {
                        const quoteType = quote?.[1]?.quote_type;
                        // Skip entries with no quote_type (old-style type-keyed objects lack
                        // the field on the value; passing undefined to getAggregateQuoteByType
                        // can accidentally match everything and cause a charAt crash)
                        if (!quoteType) return;

                        // Skip types that are not required — disabled departments must not
                        // contribute designers or inflate the assignee count
                        const aggregated = getAggregateQuoteByType(assignment.quotes, quoteType);
                        if (!aggregated) return;

                        const designers = aggregated.designers || [];
                        // Deduplicate: show each unique userId once per type
                        const seen = new Set();
                        designers.forEach(designer => {
                            if (designer.userId && designer.name && !seen.has(designer.userId)) {
                                seen.add(designer.userId);
                                assignees.push({
                                    name: designer.name,
                                    role: `${quoteType.charAt(0).toUpperCase() + quoteType.slice(1)} Designer`,
                                    type: quoteType,
                                    child_id: quote?.[1]?.child_unique_id || null
                                });
                            }
                        });
                    });

                    // Add production workers
                    ['roof', 'wall', 'floor', 'timber'].forEach(quoteType => {
                        const production = getAggregateQuoteByType(assignment.quotes, quoteType)?.production;
                        if (production?.userId && production?.name) {
                            assignees.push({
                                name: production.name,
                                role: `${quoteType.charAt(0).toUpperCase() + quoteType.slice(1)} Production`,
                                type: quoteType
                            });
                        }
                    });
                }

                if (assignees.length === 0) {
                    return (
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-700 truncate">Unassigned</div>
                            <div className="text-xs text-gray-500 mt-0.5">No assignment</div>
                        </div>
                    );
                }

                if (assignees.length === 1) {
                    return (
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-blue-700 truncate">{assignees[0].name}</div>
                            <div className="text-xs text-blue-500 mt-0.5">{assignees[0].role}</div>
                        </div>
                    );
                }

                // Multiple assignees - show with tooltip
                return <AssigneeTooltip assignment={assignment} assignees={assignees} />;
            },
            size: 160,
            minSize: 120,
            maxSize: 200,
        },
        {
            id: 'priority',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiAlertCircle className="w-3 h-3 mr-1 text-zinc-100" />
                    Priority
                </div>
            ),
            cell: ({ row }) => {
                const priority = getPriorityIndicator(row.original);
                const urgentQuote = row.original.jobInformation?.urgentQuote || row.original.urgent_quote;
                const urgentDesign = row.original.jobInformation?.urgentDesign || row.original.urgent_design;
                return (
                    <div className="flex items-center justify-center gap-1">
                        <div className={`w-3 h-3 rounded-full ${priority.color} shadow-sm`} title={priority.label}></div>
                        <div className="flex gap-1">
                            {urgentQuote && (
                                <span className="px-1 py-0.5 text-xs bg-red-100 text-red-700 rounded border" title="Urgent Quote">
                                    UQ
                                </span>
                            )}
                            {urgentDesign && (
                                <span className="px-1 py-0.5 text-xs bg-orange-100 text-orange-700 rounded border" title="Urgent Design">
                                    UD
                                </span>
                            )}
                        </div>
                    </div>
                );
            },
            size: 100,
            minSize: 80,
            maxSize: 140,
        },
        {
            id: 'invoiced',
            accessorKey: 'invoiced',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiDollarSign className="w-3 h-3 mr-1 text-zinc-100" />
                    Invoice
                </div>
            ),
            cell: ({ row }) => (
                <div className="flex justify-center">
                    <InvoiceStatusTooltip assignment={row.original} />
                </div>
            ),
            size: 110,
            minSize: 90,
            maxSize: 130,
        },
        {
            id: 'quoteSent',
            accessorKey: 'quote_sent',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiSend className="w-3 h-3 mr-1 text-zinc-100" />
                    Quote Sent
                </div>
            ),
            cell: ({ row }) => {
                const quoteSent = row.original.quote_sent || row.original.quoteSent;
                return (
                    <div className="flex justify-center">
                        {quoteSent ? (
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-xs font-medium text-green-700">Sent</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                <span className="text-xs font-medium text-gray-600">Not Sent</span>
                            </div>
                        )}
                    </div>
                );
            },
            size: 130,
            minSize: 90,
            maxSize: 130,
        },
        {
            id: 'createdAt',
            accessorKey: 'createdAt',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiCalendar className="w-3 h-3 mr-1 text-zinc-100" />
                    Created
                </div>
            ),
            cell: ({ row }) => {
                const date = new Date(row.original.createdAt || row.original.created_at);
                return (
                    <div className="text-center">
                        <div className="text-sm font-medium text-gray-700">
                            {date.toLocaleDateString('en-AU')}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                            {date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    </div>
                );
            },
            size: 110,
            minSize: 90,
            maxSize: 130,
        },
        {
            id: 'etaDate',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiCalendar className="w-3 h-3 mr-1 text-zinc-100" />
                    ETA Date
                </div>
            ),
            cell: ({ row }) => (
                <ETADatesToolTip assignment={row.original} />
            ),
            size: 120,
            minSize: 100,
            maxSize: 140,
        },
        {
            id: 'note',
            header: () => (
                <div className="flex items-center justify-center">
                    <FiMessageSquare className="w-3 h-3 mr-1 text-zinc-100" />
                    Note
                </div>
            ),
            cell: ({ row }) => <NotesCell row={row} />,
            size: 110,
            minSize: 80,
            maxSize: 200,
        },
    ], [
        accordionStates,
        columnVisibility,
        notesOverrides,
        availableDesigners,
        userProfile?.role,
        invoiceCompany,
    ]);

    const table = useReactTable({
        data: expandedAssignments,
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        onColumnVisibilityChange: setColumnVisibility,
        onColumnSizingChange: setColumnSizing,
        defaultColumn: {
            minSize: 60,
            maxSize: 600,
        },
        state: {
            columnVisibility,
            columnSizing,
        },
    });

    const LoadingRow = () => (
        <tr>
            <td colSpan={columns.length} className="px-4 py-8 text-center">
                <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-gray-600">Loading more assignments...</span>
                </div>
            </td>
        </tr>
    );

    const AssigneeTooltip = ({ assignment, assignees }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, showAbove: false });
        const [triggerRef, setTriggerRef] = useState(null);

        useEffect(() => {
            const close = () => setIsHovered(false);
            window.addEventListener('job-editor-close-tooltips', close);
            return () => window.removeEventListener('job-editor-close-tooltips', close);
        }, []);

        useEffect(() => {
            if (!isHovered || !triggerRef) return;

            const updatePosition = () => {
                const rect = triggerRef.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.bottom;
                const spaceAbove = rect.top;
                const tooltipHeight = 200;

                setTooltipPosition({
                    x: rect.left,
                    y: spaceBelow < tooltipHeight && spaceAbove > spaceBelow
                        ? rect.top - 10
                        : rect.bottom + 10,
                    showAbove: spaceBelow < tooltipHeight && spaceAbove > spaceBelow
                });
            };

            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);

            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }, [isHovered, triggerRef]);

        const getQuoteIcon = (quoteType) => {
            switch (quoteType) {
                case 'roof': return <FiHome className="w-3 h-3" />;
                case 'wall': return <BrickWall className="w-3 h-3" />;
                case 'floor': return <Grid2X2 className="w-3 h-3" />;
                case 'timber': return <GiWoodBeam className="w-3 h-3" />;
                default: return <FiLayers className="w-3 h-3" />;
            }
        };

        const typeOrder = {
            roof: 1,
            wall: 2,
            floor: 3,
            timber: 4
        };

        const TooltipContent = () => (
            <div
                className="fixed z-[9999] w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-3 pointer-events-none"
                style={{
                    left: `${tooltipPosition.x}px`,
                    [tooltipPosition.showAbove ? 'bottom' : 'top']: tooltipPosition.showAbove
                        ? `${window.innerHeight - tooltipPosition.y}px`
                        : `${tooltipPosition.y}px`,
                    transform: tooltipPosition.x > window.innerWidth - 256 ? 'translateX(-100%)' : 'none'
                }}
            >
                <div
                    className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${tooltipPosition.showAbove ? 'border-t-0 border-l-0' : 'border-b-0 border-r-0'
                        }`}
                    style={{
                        [tooltipPosition.showAbove ? 'bottom' : 'top']: '-6px',
                        left: tooltipPosition.x > window.innerWidth - 256 ? 'calc(100% - 20px)' : '12px'
                    }}
                ></div>

                <div className="space-y-2">
                    <h4 className="font-medium text-gray-900 text-xs">Assignees</h4>
                    <div className="space-y-1.5 border-t pt-1.5">
                        {[...assignees]
                            .sort((a, b) => {
                                const orderA = typeOrder[a.type?.toLowerCase()] ?? 999;
                                const orderB = typeOrder[b.type?.toLowerCase()] ?? 999;
                                return orderA - orderB;
                            }).map((assignee, index) => (
                                <div key={index} className="flex items-center justify-between text-xs">
                                    <div className='flex flex-row items-center'>
                                        {getQuoteIcon(assignee.type)}
                                        <span className="font-medium text-gray-700 ml-1 capitalize">{assignee.type} {assignee?.child_id && `(${assignee?.child_id})`}</span>
                                    </div>
                                    <span className="text-gray-600 truncate max-w-32">{assignee.name}</span>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        );

        return (
            <>
                <div
                    ref={setTriggerRef}
                    className="min-w-0 flex-1 cursor-help"
                    onPointerEnter={() => setIsHovered(true)}
                    onPointerLeave={() => setIsHovered(false)}
                    onPointerCancel={() => setIsHovered(false)}
                >
                    <div className="text-sm font-medium text-blue-500 truncate">Multiple Assignees</div>
                    <div className="text-xs text-gray-500 mt-0.5">{assignees.length} people assigned</div>
                </div>
                {isHovered && createPortal(<TooltipContent />, document.body)}
            </>
        );
    };

    const InvoiceStatusTooltip = ({ assignment }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, showAbove: false });
        const [triggerRef, setTriggerRef] = useState(null);

        useEffect(() => {
            const close = () => setIsHovered(false);
            window.addEventListener('job-editor-close-tooltips', close);
            return () => window.removeEventListener('job-editor-close-tooltips', close);
        }, []);

        useEffect(() => {
            if (isHovered && triggerRef) {
                const rect = triggerRef.getBoundingClientRect();
                const tooltipHeight = 180;
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - rect.bottom;
                const spaceAbove = rect.top;
                const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;

                setTooltipPosition({
                    x: rect.left,
                    y: showAbove ? rect.top - 8 : rect.bottom + 8,
                    showAbove
                });
            }
        }, [isHovered, triggerRef]);

        const getQuoteIcon = (type) => {
            if (type === 'roof') return <FiHome className="w-3 h-3" />;
            if (type === 'wall') return <BrickWall className="w-3 h-3" />;
            if (type === 'floor') return <Grid2X2 className="w-3 h-3" />;
            if (type === 'timber') return <GiWoodBeam className="w-3 h-3" />;
            return <FiDollarSign className="w-3 h-3" />;
        };

        const getQuoteLabel = (quote, fallbackType) => {
            return (
                quote?.child_unique_id ||
                quote?.childUniqueId ||
                quote?.child_id ||
                quote?.childId ||
                fallbackType ||
                quote?.quote_type ||
                quote?.quoteType ||
                'Quote'
            );
        };

        const getInvoiceStatuses = () => {
            const quotes = assignment.quotes || {};

            if (assignment._isAccordionChild) {
                const quote = assignment._quoteData;
                const type = assignment._quoteType || quote?.quote_type || quote?.quoteType;

                if (!quote) return [];

                return [{
                    id: quote.id || assignment._displayId || type,
                    type,
                    label: getQuoteLabel(quote, type),
                    text: getInvoiceStatusText(quote, invoiceCompany),
                    sent: isQuoteInvoiceSent(quote, invoiceCompany),
                    sourceIcon: getInvoiceSourceIcon(quote, invoiceCompany, 'sm'),
                    icon: getQuoteIcon(type),
                }];
            }

            return Object.values(quotes || {})
                .filter(q => q?.isRequired || q?.is_required)
                .map(q => {
                    const type = q.quote_type || q.quoteType;

                    return {
                        id: q.id || q.child_unique_id || type,
                        type,
                        label: getQuoteLabel(q, type),
                        text: getInvoiceStatusText(q, invoiceCompany),
                        sent: isQuoteInvoiceSent(q, invoiceCompany),
                        sourceIcon: getInvoiceSourceIcon(q, invoiceCompany, 'sm'),
                        icon: getQuoteIcon(type),
                    };
                });
        };

        const invoiceStatuses = getInvoiceStatuses();
        const allInvoiced = invoiceStatuses.length > 0 && invoiceStatuses.every(s => s.sent);
        const someInvoiced = invoiceStatuses.some(s => s.sent);

        const TooltipContent = () => (
            <div
                className="fixed z-[9999] w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-3 pointer-events-none"
                style={{
                    left: `${tooltipPosition.x}px`,
                    top: `${tooltipPosition.y}px`,
                    transform: `${tooltipPosition.x > window.innerWidth - 288 ? 'translateX(-100%)' : 'translateX(0)'} ${tooltipPosition.showAbove ? 'translateY(-100%)' : 'translateY(0)'}`
                }}
            >
                <div
                    className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${tooltipPosition.showAbove ? 'border-t-0 border-l-0' : 'border-b-0 border-r-0'}`}
                    style={{
                        [tooltipPosition.showAbove ? 'bottom' : 'top']: '-6px',
                        left: tooltipPosition.x > window.innerWidth - 288 ? 'calc(100% - 20px)' : '12px'
                    }}
                />

                <div className="space-y-2">
                    <h4 className="font-medium text-gray-900 text-xs">Invoice Status</h4>

                    {invoiceStatuses.length > 0 ? (
                        <div className="space-y-1.5">
                            {invoiceStatuses.map((status) => (
                                <div key={status.id} className="flex items-center justify-between gap-3 text-xs">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className={status.sent ? 'text-green-600' : 'text-gray-400'}>
                                            {status.icon}
                                        </div>

                                        <div className="min-w-0">
                                            <div className="font-medium text-gray-800 truncate">
                                                {status.label}
                                            </div>
                                            <div className="text-[10px] text-gray-500 capitalize">
                                                {status.type}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1.5 shrink-0">
                                        {status.sourceIcon}
                                        <div className={`w-2 h-2 rounded-full ${status.sent ? 'bg-green-500' : 'bg-gray-400'}`} />
                                        <span className={`font-medium ${status.sent ? 'text-green-700' : 'text-gray-600'}`}>
                                            {status.text}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-gray-500 italic text-center py-1">No quote invoices</p>
                    )}
                </div>
            </div>
        );

        let invoicedText = "All Sent";
        if (allInvoiced && assignment?._isAccordionChild) invoicedText = "Sent";

        return (
            <>
                <div
                    ref={setTriggerRef}
                    className="flex items-center gap-1 cursor-help"
                    onPointerEnter={() => setIsHovered(true)}
                    onPointerLeave={() => setIsHovered(false)}
                    onPointerCancel={() => setIsHovered(false)}
                >
                    {invoiceStatuses.length > 0 ? (
                        <>
                            <div className={`w-2 h-2 rounded-full ${allInvoiced ? 'bg-green-500' : someInvoiced ? 'bg-yellow-500' : 'bg-gray-400'}`} />
                            <span className={`text-xs font-medium ${allInvoiced ? 'text-green-700' : someInvoiced ? 'text-yellow-700' : 'text-gray-600'}`}>
                                {allInvoiced ? invoicedText : someInvoiced ? 'Partial' : 'Pending'}
                            </span>
                        </>
                    ) : (
                        <span className="text-xs text-gray-500">-</span>
                    )}
                </div>

                {isHovered && invoiceStatuses.length > 0 && createPortal(<TooltipContent />, document.body)}
            </>
        );
    };

    const ETADatesToolTip = ({ assignment }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, showAbove: false });
        const [triggerRef, setTriggerRef] = useState(null);

        useEffect(() => {
            const close = () => setIsHovered(false);
            window.addEventListener('job-editor-close-tooltips', close);
            return () => window.removeEventListener('job-editor-close-tooltips', close);
        }, []);

        useEffect(() => {
            if (isHovered && triggerRef) {
                const rect = triggerRef.getBoundingClientRect();
                const tooltipHeight = 180;
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - rect.bottom;
                const spaceAbove = rect.top;
                const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;

                setTooltipPosition({
                    x: rect.left,
                    y: showAbove ? rect.top - 8 : rect.bottom + 8,
                    showAbove
                });
            }
        }, [isHovered, triggerRef]);

        const getETADates = () => {
            const quoteCategories = [];
            const quotes = assignment.quotes || {};

            // Roof quote
            if (quotes.roof?.isRequired || quotes.roof?.is_required) {
                const finishDate = quotes.roof.expectedFinishDate || quotes.roof.expected_finish_date;
                const deliveryDate = quotes.roof.expectedDeliveryDate || quotes.roof.expected_delivery_date;
                quoteCategories.push({
                    type: 'roof',
                    label: 'Roof',
                    icon: <FiHome className="w-3 h-3" />,
                    finishDate: finishDate,
                    deliveryDate: deliveryDate
                });
            }

            // Wall quote
            if (quotes.wall?.isRequired || quotes.wall?.is_required) {
                const finishDate = quotes.wall.expectedFinishDate || quotes.wall.expected_finish_date;
                const deliveryDate = quotes.wall.expectedDeliveryDate || quotes.wall.expected_delivery_date;
                quoteCategories.push({
                    type: 'wall',
                    label: 'Wall',
                    icon: <BrickWall className="w-3 h-3" />,
                    finishDate: finishDate,
                    deliveryDate: deliveryDate
                });
            }

            // Floor quote
            if (quotes.floor?.isRequired || quotes.floor?.is_required) {
                const finishDate = quotes.floor.expectedFinishDate || quotes.floor.expected_finish_date;
                const deliveryDate = quotes.floor.expectedDeliveryDate || quotes.floor.expected_delivery_date;
                quoteCategories.push({
                    type: 'floor',
                    label: 'Floor',
                    icon: <Grid2X2 className="w-3 h-3" />,
                    finishDate: finishDate,
                    deliveryDate: deliveryDate
                });
            }

            // Estimation (no delivery date for estimation)
            const estimationDate = assignment.expectedEstimationFinishDate || assignment.expected_estimation_finish_date;
            if (estimationDate) {
                quoteCategories.push({
                    type: 'estimation',
                    label: 'Estimation',
                    icon: <FiDollarSign className="w-3 h-3" />,
                    finishDate: estimationDate,
                    deliveryDate: null
                });
            }

            return quoteCategories;
        };

        const etaDates = getETADates();
        // Get all dates (both finish and delivery) for finding the latest
        const allDates = etaDates.flatMap(cat => [cat.finishDate, cat.deliveryDate].filter(Boolean));
        const latestDate = allDates.length > 0 ?
            new Date(Math.max(...allDates.map(d => new Date(d)))) : null;

        const formatDateShort = (date) => {
            if (!date) return '-';
            return new Date(date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
        };

        const getDaysRemaining = (date) => {
            if (!date) return null;
            const days = Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
            return days;
        };

        const TooltipContent = () => (
            <div
                className="fixed z-[9999] w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-3 pointer-events-none"
                style={{
                    left: `${tooltipPosition.x}px`,
                    top: tooltipPosition.showAbove ? `${tooltipPosition.y}px` : `${tooltipPosition.y}px`,
                    transform: `${tooltipPosition.x > window.innerWidth - 320 ? 'translateX(-100%)' : 'translateX(0)'} ${tooltipPosition.showAbove ? 'translateY(-100%)' : 'translateY(0)'}`
                }}
            >
                <div
                    className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${tooltipPosition.showAbove ? 'border-t-0 border-l-0' : 'border-b-0 border-r-0'}`}
                    style={{
                        [tooltipPosition.showAbove ? 'bottom' : 'top']: '-6px',
                        left: tooltipPosition.x > window.innerWidth - 320 ? 'calc(100% - 20px)' : '12px'
                    }}
                ></div>

                <div className="space-y-2">
                    <h4 className="font-medium text-gray-900 text-sm mb-2">ETA Dates</h4>
                    {etaDates.length > 0 ? (
                        <div className="space-y-3">
                            {etaDates.map((category, index) => {
                                const hasFinish = !!category.finishDate;
                                const hasDelivery = !!category.deliveryDate;

                                return (
                                    <div key={index} className="border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <div className="text-blue-600">
                                                {category.icon}
                                            </div>
                                            <span className="font-semibold text-sm text-gray-800">{category.label}</span>
                                        </div>
                                        <div className="ml-5 space-y-1">
                                            {hasFinish && (
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-gray-600">Finish Date:</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-gray-900 font-medium">{formatDateShort(category.finishDate)}</span>
                                                        {(() => {
                                                            const days = getDaysRemaining(category.finishDate);
                                                            const isOverdue = days < 0;
                                                            return (
                                                                <span className={`text-xs ${isOverdue ? 'text-red-600' : days <= 3 ? 'text-orange-600' : 'text-gray-500'}`}>
                                                                    ({isOverdue ? `${Math.abs(days)}d overdue` : `${days}d`})
                                                                </span>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            )}
                                            {hasDelivery && (
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-gray-600">Delivery Date:</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-gray-900 font-medium">{formatDateShort(category.deliveryDate)}</span>
                                                        {(() => {
                                                            const days = getDaysRemaining(category.deliveryDate);
                                                            const isOverdue = days < 0;
                                                            return (
                                                                <span className={`text-xs ${isOverdue ? 'text-red-600' : days <= 3 ? 'text-orange-600' : 'text-gray-500'}`}>
                                                                    ({isOverdue ? `${Math.abs(days)}d overdue` : `${days}d`})
                                                                </span>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            )}
                                            {!hasFinish && !hasDelivery && (
                                                <span className="text-xs text-gray-400 italic">No dates set</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-xs text-gray-500 italic text-center py-2">No quotes available</p>
                    )}
                </div>
            </div>
        );

        const estimationDate = assignment.expectedEstimationFinishDate || assignment.expected_estimation_finish_date;

        return (
            <>
                <div
                    ref={setTriggerRef}
                    className="text-center cursor-help"
                    onPointerEnter={() => setIsHovered(true)}
                    onPointerLeave={() => setIsHovered(false)}
                    onPointerCancel={() => setIsHovered(false)}
                >
                    {estimationDate ? (
                        <>
                            <div className="text-sm font-medium text-gray-700">
                                {formatDateShort(estimationDate)}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                                {getDaysRemaining(estimationDate)} days
                            </div>
                        </>
                    ) : (
                        <div className="text-sm text-gray-500 px-4 py-2">-</div>
                    )}
                </div>
                {isHovered && etaDates.length > 0 && createPortal(<TooltipContent />, document.body)}
            </>
        );
    };

    const QuotesHoverTooltip = ({ assignment }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, showAbove: false });
        const [triggerRef, setTriggerRef] = useState(null);

        useEffect(() => {
            const close = () => setIsHovered(false);
            window.addEventListener('job-editor-close-tooltips', close);
            return () => window.removeEventListener('job-editor-close-tooltips', close);
        }, []);

        useEffect(() => {
            if (isHovered && triggerRef) {
                const rect = triggerRef.getBoundingClientRect();
                const tooltipHeight = 200;
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - rect.bottom;
                const spaceAbove = rect.top;

                const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;

                setTooltipPosition({
                    x: rect.left,
                    y: showAbove ? rect.top - 8 : rect.bottom + 8,
                    showAbove
                });
            }
        }, [isHovered, triggerRef]);

        const getQuoteStatusColor = (quoteType) => {
            if (quoteType === 'estimation') {
                if (!assignment.estimator?.user_id) return 'text-red-500';
                if (!assignment.estimator?.is_completed) return 'text-yellow-500';
                return 'text-green-500';
            }

            if (isFileOnlyQuoteType(quoteType)) {
                const quote = getAggregateQuoteByType(assignment.quotes, quoteType);
                return isFileOnlyQuoteCompleted(quote)
                    ? "text-green-500"
                    : "text-red-500";
            }

            const quote = getAggregateQuoteByType(assignment.quotes, quoteType);
            if (!quote) return "text-gray-300";

            if (assignment?.jobType === "Estimation" || assignment?.job_type === "Estimation") {
                return "text-gray-300";
            }

            const jobType = (assignment.jobType ?? assignment.job_type ?? '').toLowerCase();
            const hasDesign = jobType.includes('design');
            const hasEstimation = jobType.includes('estimation');

            if (hasDesign) {
                // For D&E jobs: show estimation status while estimation is in progress,
                // then switch to design status once estimation is completed.
                if (hasEstimation) {
                    const estimDone = quote.estimationStatus === 'completed';
                    const activeStatus = estimDone
                        ? (quote.designStatus || quote.design_status)
                        : quote.estimationStatus;
                    switch (activeStatus) {
                        case 'waiting_assignment':
                        case 'waiting_design': return 'text-red-500';
                        case 'in_progress': return 'text-yellow-500';
                        case 'completed': return 'text-green-500';
                        default: return 'text-gray-400';
                    }
                }
                // Design-only: use design status directly.
                const status = quote.designStatus || quote.design_status;
                switch (status) {
                    case 'waiting_assignment':
                    case 'waiting_design': return 'text-red-500';
                    case 'in_progress': return 'text-yellow-500';
                    case 'completed': return 'text-green-500';
                    default: return 'text-gray-400';
                }
            }

            if (hasEstimation) {
                // Estimation-only: colour by estimation status per department.
                const status = quote.estimationStatus;
                switch (status) {
                    case 'waiting_assignment': return 'text-red-500';
                    case 'in_progress': return 'text-yellow-500';
                    case 'completed': return 'text-green-500';
                    default: return 'text-gray-400';
                }
            }

            return 'text-blue-500';
        };

        const getEstimationStatus = () => {
            if (!assignment.estimator?.user_id) return 'waiting_assignment';
            if (!assignment.estimator?.is_completed) return 'in_progress';
            return 'completed';
        };

        const estimationStatus = getEstimationStatus();

        const getQuoteIcon = (quoteType) => getEditorQuoteIcon(quoteType, 12);

        const getStatusDot = (status) => {
            switch (status) {
                case 'completed': return 'bg-green-500';
                case 'in_progress': return 'bg-yellow-500';
                case 'waiting_assignment': return 'bg-red-500';
                case 'waiting_design': return 'bg-red-500';
                default: return 'bg-gray-400';
            }
        };


        const getRequiredQuotes = () => {
            const result = [];

            Object.values(assignment.quotes || {})
                .filter(q => q?.isRequired || q?.is_required)
                .sort((a, b) => {
                    const aType = a.quote_type || a.quoteType;
                    const bType = b.quote_type || b.quoteType;
                    return (QUOTE_ORDER[aType] ?? 99) - (QUOTE_ORDER[bType] ?? 99);
                })
                .forEach(q => {
                    const type = q.quote_type || q.quoteType;
                    if (!type) return;

                    const aggregated = getAggregateQuoteByType(assignment.quotes, type);
                    if (!aggregated) return;

                    if (result.some(item => item.type === type)) return;

                    result.push({
                        type,
                        ...aggregated,
                        designStatus: isFileOnlyQuoteType(type)
                            ? (isFileOnlyQuoteCompleted(aggregated) ? "completed" : "waiting_assignment")
                            : type === "timber"
                                ? "not_required"
                                : (aggregated.designStatus || aggregated.design_status),
                    });
                });

            return result;
        };

        const requiredQuotes = getRequiredQuotes();
        const needsEstimation = (assignment.jobType === 'Estimation' || assignment.jobType === 'Design & Estimation') ||
            (assignment.job_type === 'Estimation' || assignment.job_type === 'Design & Estimation');




        const TooltipContent = () => (
            <div
                className="fixed z-[9999] w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-3 pointer-events-none"
                style={{
                    left: `${tooltipPosition.x}px`,
                    top: tooltipPosition.showAbove ? `${tooltipPosition.y}px` : `${tooltipPosition.y}px`,
                    transform: `${tooltipPosition.x > window.innerWidth - 256 ? 'translateX(-100%)' : 'translateX(0)'} ${tooltipPosition.showAbove ? 'translateY(-100%)' : 'translateY(0)'}`
                }}
            >
                <div
                    className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${tooltipPosition.showAbove ? 'border-t-0 border-l-0' : 'border-b-0 border-r-0'
                        }`}
                    style={{
                        [tooltipPosition.showAbove ? 'bottom' : 'top']: '-6px',
                        left: tooltipPosition.x > window.innerWidth - 256 ? 'calc(100% - 20px)' : '12px'
                    }}
                ></div>

                <div className="space-y-2">
                    <h4 className="font-medium text-gray-900 text-xs">Status</h4>

                    {requiredQuotes.length > 0 && (
                        <div className="space-y-1">
                            {requiredQuotes.map((quote, index) => {
                                const isMulti = quote._count > 1;
                                const assignedCount = quote.designers?.filter(d => d.userId)?.length ?? 0;
                                const designerLabel = quote.type === 'timber'
                                    ? 'N/A'
                                    : assignedCount === 0
                                        ? (assignment?.job_type === "Estimation" || assignment?.jobType === "Estimation") ? "Not Required" : "Unassigned"
                                        : isMulti
                                            ? `${assignedCount} designer${assignedCount > 1 ? 's' : ''}`
                                            : quote.designers[0].name.split(' ')[0];

                                return (
                                    <div key={index} className="flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-2">
                                            <div className={`${getQuoteStatusColor(quote.type)}`}>
                                                {getQuoteIcon(quote.type)}
                                            </div>
                                            <span className="font-medium capitalize">
                                                {getQuoteLabel(quote.type)}
                                                {isMulti && (
                                                    <span className="ml-1 text-gray-400 font-normal">×{quote._count}</span>
                                                )}
                                            </span>
                                            <div className={`w-2 h-2 rounded-full ${getStatusDot(quote.designStatus)}`}></div>
                                        </div>
                                        <span className={`truncate max-w-24 ${assignedCount === 0 ? 'text-red-400' : 'text-gray-600'}`}>
                                            {designerLabel}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {needsEstimation && (
                        <div className="border-t border-gray-100 pt-2">
                            <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                    <FiDollarSign className={`w-3 h-3 ${getQuoteStatusColor('estimation')}`} />
                                    <span className="font-medium">Estimation</span>
                                    <div className={`w-2 h-2 rounded-full ${getStatusDot(estimationStatus)}`}></div>
                                </div>
                                <span className="text-gray-600 truncate max-w-20">
                                    {assignment.estimator?.name ? assignment.estimator.name.split(' ')[0] : "Unassigned"}
                                </span>
                            </div>
                        </div>
                    )}



                    {requiredQuotes.length === 0 && !needsEstimation && (
                        <p className="text-xs text-gray-500 italic text-center py-1">
                            No quotes required
                        </p>
                    )}

                    <div className="border-t border-gray-100 pt-2 flex items-center gap-3 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span>Completed</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                            <span>In Progress</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span>Unassigned</span>
                        </div>
                    </div>
                </div>
            </div>
        );

        // Helper: render one icon with optional ×N badge for multi-quote types
        const QuoteIcon = ({ type, icon: Icon }) => {
            const q = getAggregateQuoteByType(assignment.quotes, type);
            if (!q) return null;
            const count = q._count ?? 1;
            return (
                <div className="relative flex items-center">
                    <Icon
                        className={`w-4 h-4 ${getQuoteStatusColor(type)}`}
                        title={`${type} (${count}) – ${q.designStatus || q.design_status || 'unknown'}`}
                    />
                    {count > 1 && (
                        <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-gray-600 text-white text-[9px] font-bold leading-none px-0.5">
                            {count}
                        </span>
                    )}
                </div>
            );
        };

        return (
            <>
                <div
                    ref={setTriggerRef}
                    className="flex items-center gap-1.5 flex-shrink-0 cursor-help"
                    onPointerEnter={() => {
                        window.dispatchEvent(new Event('job-editor-close-tooltips'));
                        requestAnimationFrame(() => setIsHovered(true));
                    }}
                    onPointerLeave={() => setIsHovered(false)}
                    onPointerCancel={() => setIsHovered(false)}
                >
                    <QuoteIcon type="beams" icon={GiWoodPile} />
                    <QuoteIcon type="flooring_sheets" icon={SquareStack} />
                    <QuoteIcon type="roof" icon={FiHome} />
                    <QuoteIcon type="wall" icon={BrickWall} />
                    <QuoteIcon type="floor" icon={Grid2X2} />
                    <QuoteIcon type="timber" icon={GiWoodBeam} />


                    {needsEstimation && (
                        <FiDollarSign
                            className={`w-4 h-4 ${getQuoteStatusColor('estimation')}`}
                            title="Estimation status"
                        />
                    )}
                </div>

                {isHovered && createPortal(<TooltipContent />, document.body)}
            </>
        );
    };

    const SingleQuoteDisplay = ({ assignment, quoteType }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, showAbove: false });
        const [triggerRef, setTriggerRef] = useState(null);

        useEffect(() => {
            const close = () => setIsHovered(false);
            window.addEventListener('job-editor-close-tooltips', close);
            return () => window.removeEventListener('job-editor-close-tooltips', close);
        }, []);

        useEffect(() => {
            if (isHovered && triggerRef) {
                const rect = triggerRef.getBoundingClientRect();
                const tooltipHeight = 150;
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - rect.bottom;
                const spaceAbove = rect.top;
                const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;

                setTooltipPosition({
                    x: rect.left,
                    y: showAbove ? rect.top - 8 : rect.bottom + 8,
                    showAbove
                });
            }
        }, [isHovered, triggerRef]);

        const getQuoteStatusColor = () => {
            // Use _quoteData first (specific child data), then fall back to aggregate
            const quote = assignment._quoteData || getAggregateQuoteByType(assignment.quotes, quoteType);
            if (!quote?.isRequired && !quote?.is_required) return 'text-gray-300';

            const status = quote.designStatus || quote.design_status;
            switch (status) {
                case 'waiting_assignment':
                case 'waiting_design': return 'text-red-500';
                case 'in_progress': return 'text-yellow-500';
                case 'completed': return 'text-green-500';
                default: return 'text-gray-400';
            }
        };

        const getQuoteIcon = () => {
            switch (quoteType) {
                case 'roof': return <FiHome className="w-5 h-5" />;
                case 'wall': return <BrickWall className="w-5 h-5" />;
                case 'floor': return <Grid2X2 className="w-5 h-5" />;
                case 'timber': return <GiWoodBeam className="w-5 h-5" />;
                default: return <FiLayers className="w-5 h-5" />;
            }
        };

        const getStatusDot = (status) => {
            switch (status) {
                case 'completed': return 'bg-green-500';
                case 'in_progress': return 'bg-yellow-500';
                case 'waiting_assignment': return 'bg-red-500';
                case 'waiting_design': return 'bg-red-500';
                default: return 'bg-gray-400';
            }
        };

        const quote = assignment._quoteData || getAggregateQuoteByType(assignment.quotes, quoteType);



        const TooltipContent = () => (
            <div
                className="fixed z-[9999] w-64 bg-white border border-gray-200 rounded-lg shadow-xl p-3 pointer-events-none"
                style={{
                    left: `${tooltipPosition.x}px`,
                    top: tooltipPosition.showAbove ? `${tooltipPosition.y}px` : `${tooltipPosition.y}px`,
                    transform: `${tooltipPosition.x > window.innerWidth - 256 ? 'translateX(-100%)' : 'translateX(0)'} ${tooltipPosition.showAbove ? 'translateY(-100%)' : 'translateY(0)'}`
                }}
            >
                <div
                    className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${tooltipPosition.showAbove ? 'border-t-0 border-l-0' : 'border-b-0 border-r-0'}`}
                    style={{
                        [tooltipPosition.showAbove ? 'bottom' : 'top']: '-6px',
                        left: tooltipPosition.x > window.innerWidth - 256 ? 'calc(100% - 20px)' : '12px'
                    }}
                ></div>

                <div className="space-y-2">
                    <h4 className="font-medium text-gray-900 text-xs capitalize">{quoteType} Quote Status</h4>

                    <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                            <span className="font-medium">Status:</span>
                            <div className={`w-2 h-2 rounded-full ${getStatusDot(quote?.designStatus || quote?.design_status)}`}></div>
                        </div>
                        <span className="text-gray-600 capitalize">
                            {(quote?.designStatus || quote?.design_status)?.replace('_', ' ') || 'N/A'}
                        </span>
                    </div>

                    {quote?.designers && quote.designers.length > 0 && (
                        <div className="border-t border-gray-100 pt-2">
                            <span className="text-xs font-medium text-gray-700">Designers:</span>
                            <div className="mt-1 space-y-1">
                                {quote.designers.map((designer, idx) => (
                                    <div key={idx} className="flex items-center justify-between text-xs">
                                        <span className="text-gray-600">{designer.name}</span>
                                        {(designer.isCompleted || designer.is_completed) && (
                                            <FiCheckCircle className="w-3 h-3 text-green-500" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {quoteType === 'timber' ? (
                        <p className="text-xs text-gray-500 italic">Timber jobs don't require designers</p>
                    ) : (
                        (!quote?.designers || quote.designers.length === 0) && (
                            <p className="text-xs text-red-500 italic">No designer assigned</p>
                        )
                    )}
                </div>
            </div>
        );

        return (
            <>
                <div
                    ref={setTriggerRef}
                    className={`cursor-help ${getQuoteStatusColor()}`}
                    onPointerEnter={() => {
                        window.dispatchEvent(new Event('job-editor-close-tooltips'));
                        requestAnimationFrame(() => setIsHovered(true));
                    }}
                    onPointerLeave={() => setIsHovered(false)}
                    onPointerCancel={() => setIsHovered(false)}
                >
                    {getQuoteIcon()}
                </div>
                {isHovered && createPortal(<TooltipContent />, document.body)}
            </>
        );
    };

    const NotesCell = ({ row }) => {
        const isChild = row.original._isAccordionChild;
        const rowData = row.original;
        const jobId = rowData._originalId || rowData._id || rowData.id;
        const quoteId = rowData._quoteData?.id;
        const noteKey = isChild && quoteId ? `q_${quoteId}` : `j_${jobId}`;

        const initialNotes = isChild
            ? (rowData._quoteData?.editorNote || [])
            : (rowData.editorNote || []);
        const notes = notesOverrides[noteKey] ?? initialNotes;

        const [isOpen, setIsOpen] = useState(false);
        const [inputValue, setInputValue] = useState('');
        const [isSaving, setIsSaving] = useState(false);
        const [popoverPos, setPopoverPos] = useState({ x: 0, y: 0, showAbove: false });
        const cellRef = useRef(null);
        const popoverRef = useRef(null);


        useEffect(() => {
            if (!isOpen) return;
            const handler = (e) => {
                if (
                    popoverRef.current && !popoverRef.current.contains(e.target) &&
                    cellRef.current && !cellRef.current.contains(e.target)
                ) setIsOpen(false);
            };
            document.addEventListener('mousedown', handler);
            return () => document.removeEventListener('mousedown', handler);
        }, [isOpen]);

        const saveNotes = async (newNotes) => {
            setIsSaving(true);
            try {
                if (isChild && quoteId) {
                    await updateQuoteEditorNote(quoteId, newNotes);
                } else {
                    await updateJobEditorNote(jobId, newNotes);
                }
                setNotesOverrides(prev => ({ ...prev, [noteKey]: newNotes }));
                Toast.success('Note saved');
            } catch {
                Toast.error('Failed to save note');
            } finally {
                setIsSaving(false);
            }
        };

        const handleOpen = (e) => {
            e.stopPropagation();
            if (isOpen) { setIsOpen(false); return; }
            const rect = cellRef.current?.getBoundingClientRect();
            if (rect) {
                const showAbove = window.innerHeight - rect.bottom < 240;
                setPopoverPos({ x: rect.left, y: showAbove ? rect.top : rect.bottom + 4, showAbove });
            }
            setIsOpen(true);
        };

        const handleAdd = async (e) => {
            e.stopPropagation();
            if (!inputValue.trim() || isSaving) return;
            await saveNotes([...notes, inputValue.trim()]);
            setInputValue('');
        };

        const handleDelete = async (e, idx) => {
            e.stopPropagation();
            if (isSaving) return;
            await saveNotes(notes.filter((_, i) => i !== idx));
        };

        const PopoverContent = () => (
            <div
                ref={popoverRef}
                className="fixed z-[9999] w-72 bg-white border border-gray-200 rounded-lg shadow-xl"
                style={{
                    left: `${popoverPos.x}px`,
                    ...(popoverPos.showAbove
                        ? { bottom: `${window.innerHeight - popoverPos.y}px` }
                        : { top: `${popoverPos.y}px` }),
                    transform: popoverPos.x > window.innerWidth - 300 ? 'translateX(-100%)' : 'none',
                }}
                onClick={e => e.stopPropagation()}
            >
                <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                        <FiMessageSquare className="w-3 h-3" />
                        Notes
                        {notes.length > 0 && (
                            <span className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded-full font-bold">{notes.length}</span>
                        )}
                    </span>
                    <button
                        onClick={e => { e.stopPropagation(); setIsOpen(false); }}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <FiX className="w-3.5 h-3.5" />
                    </button>
                </div>

                <div className="p-2 max-h-48 overflow-y-auto">
                    {notes.length === 0 ? (
                        <p className="text-xs text-gray-400 italic text-center py-2">No notes yet</p>
                    ) : (
                        <div className="space-y-1.5">
                            {notes.map((note, idx) => (
                                <div key={idx} className="flex items-start gap-2 group/note bg-gray-50 rounded px-2 py-1.5">
                                    <span className="text-xs text-gray-700 flex-1 break-words leading-relaxed">{note}</span>
                                    <button
                                        onClick={e => handleDelete(e, idx)}
                                        disabled={isSaving}
                                        className="text-gray-300 hover:text-red-500 flex-shrink-0 mt-0.5 opacity-0 group-hover/note:opacity-100 transition-opacity disabled:cursor-not-allowed"
                                    >
                                        <FiX className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="px-2 pb-2 pt-1 border-t border-gray-100 flex gap-1.5">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') handleAdd(e); }}
                        onClick={e => e.stopPropagation()}
                        placeholder="Add a note…"
                        className="flex-1 text-xs px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                        autoFocus
                    />
                    <button
                        onClick={handleAdd}
                        disabled={!inputValue.trim() || isSaving}
                        className="text-xs px-2.5 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-40 font-medium flex-shrink-0"
                    >
                        {isSaving ? '…' : 'Add'}
                    </button>
                </div>
            </div>
        );

        return (
            <>
                <div
                    ref={cellRef}
                    onClick={handleOpen}
                    className={`h-full flex items-center gap-1.5 cursor-pointer select-none px-1 py-0.5 rounded transition-colors ${notes.length > 0
                        ? 'text-blue-600 hover:bg-blue-50'
                        : 'text-gray-300 hover:text-gray-500 hover:bg-gray-50'
                        }`}
                >
                    <FiMessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
                    {notes.length > 0 && (
                        <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full leading-none">
                            {notes.length}
                        </span>
                    )}
                </div>
                {isOpen && createPortal(<PopoverContent />, document.body)}
            </>
        );
    };

    return (
        <div className="">
            <div className="editor-table-heading flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Job Editor</h3>
                <div className="flex items-center gap-2">
                    <span className="bg-orange-100 text-orange-800 text-sm font-medium px-3 py-1 rounded-full">
                        {searchQuery
                            ? `${expandedAssignments.length} rows (${filteredAssignments.length} assignments)`
                            : viewMode === 'separated'
                                ? `${expandedAssignments.length} rows (${assignments.length} assignments)`
                                : `${assignments.length} assignments`
                        }
                    </span>
                    <div className="flex items-center gap-2 ml-4">
                        <span className="text-xs text-gray-600">View:</span>
                        <div className="flex bg-gray-200 rounded-md p-0.5">
                            <button
                                onClick={() => setViewMode('separated')}
                                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${viewMode === 'separated'
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Separated
                            </button>
                            <button
                                onClick={() => setViewMode('combined')}
                                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${viewMode === 'combined'
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Combined
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div ref={columnToggleRef}>
                <ColumnToggle
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    columnVisibility={columnVisibility}
                    setColumnVisibility={setColumnVisibility}
                    setColumnSizing={setColumnSizing}
                    selectedQuoteTypes={selectedQuoteTypes}
                    setSelectedQuoteTypes={setSelectedQuoteTypes}
                    invoiceFilter={invoiceFilter}
                    setInvoiceFilter={setInvoiceFilter}
                    quoteSentFilter={quoteSentFilter}
                    setQuoteSentFilter={setQuoteSentFilter}
                    urgentFilter={urgentFilter}
                    setUrgentFilter={setUrgentFilter}
                    jobTypeFilter={jobTypeFilter}
                    setJobTypeFilter={setJobTypeFilter}
                    hideEstimationCompleted={hideEstimationCompleted}
                    setHideEstimationCompleted={setHideEstimationCompleted}
                    hideEstimatorAssigned={hideEstimatorAssigned}
                    setHideEstimatorAssigned={setHideEstimatorAssigned}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    cancelledFilter={cancelledFilter}
                    setCancelledFilter={setCancelledFilter}
                    quoteTypeFilterOptions={quoteTypeFilterOptions}
                />
            </div>

            <p className="editor-scroll-hint hidden">Swipe across for more columns · Tap a job to open</p>
            <div
                ref={tableContainerRef}
                className="editor-table-scroll bg-white border border-gray-200 rounded-lg overflow-x-hidden overflow-y-scroll shadow-sm"
                style={{ height: '80vh' }}
            >
                <table className="min-w-full" style={{ display: 'block', '--editor-table-width': `${table.getTotalSize()}px` }}>
                    <thead
                        className="bg-zinc-500 border-b-2 border-zinc-200 sticky top-0 z-10"
                        style={{ display: 'block', width: '100%' }}
                    >
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id} style={{ display: 'flex', width: '100%' }}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        className="px-4 py-3 text-xs font-semibold text-white uppercase tracking-wide border-r border-zinc-200/70 relative"
                                        style={{
                                            width: header.getSize(),
                                            flexShrink: 0, // ✅ important
                                        }}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        <div
                                            onMouseDown={header.getResizeHandler()}
                                            onTouchStart={header.getResizeHandler()}
                                            className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none bg-transparent hover:bg-blue-400 ${header.column.getIsResizing() ? 'bg-blue-500' : ''
                                                }`}
                                            style={{
                                                transform: header.column.getIsResizing() ? 'translateX(0)' : '',
                                            }}
                                        />
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody
                        ref={tableBodyRef}
                        style={{
                            display: 'block', // ✅ required for virtualization
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            width: '100%',
                            position: 'relative',
                        }}
                    >
                        {filteredAssignments.length > 0 && (
                            <>
                                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                    const row = table.getRowModel().rows[virtualRow.index];
                                    if (!row) return null;

                                    const isParent = row.original._isAccordionParent;
                                    const isChild = row.original._isAccordionChild;
                                    const jobId = row.original._originalId || row.original._id || row.original.id;

                                    const isUrgent = row.original.jobInformation?.urgentQuote ||
                                        row.original.jobInformation?.urgentDesign ||
                                        row.original.urgent_quote ||
                                        row.original.urgent_design;

                                    const navPath = isChild
                                        ? `/${companySlug}/assignment/${jobId}`
                                        : `/${companySlug}/assignment/${jobId}`;

                                    return (
                                        <AssignmentContextMenu
                                            key={row.id}
                                            assignment={row.original}
                                            onOpenAssignment={handleOpenAssignment}
                                            onEditAssignment={handleEditAssignment}
                                            onAssignDesigner={handleAssignDesigner}
                                            onForceFinish={handleForceFinish}
                                            onDuplicateJob={onDuplicateJob}
                                            onToggleHold={handleToggleHold}
                                            onToggleQuoteHold={handleToggleQuoteHold}
                                            onToggleSentProduction={handleToggleSentProduction}
                                            availableDesigners={availableDesigners}
                                            page="editor"
                                            userRole={userProfile?.role}
                                        >
                                            <tr
                                                id={isChild ? `accordion-child-${jobId}-${row.original._quoteType}` : undefined}
                                                className={`border-t cursor-pointer text-sm group bg-white
                                                    ${isChild
                                                        ? '!bg-gray-100/70 hover:bg-blue-50/40 border-gray-200/60'
                                                        : isParent
                                                            ? 'hover:bg-blue-50/50 border-gray-400/80 font-medium'
                                                            : `hover:bg-blue-50/50 border-gray-400/80`
                                                    }
                                                        ${isUrgent ? '!bg-red-50/80 !hover:bg-red-100/60 !border-gray-400/80' : ''}`}
                                                style={{
                                                    display: 'flex', // ✅ FIX TABLE BREAKING
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: `${virtualRow.size}px`,
                                                    transform: `translateY(${virtualRow.start}px)`,
                                                }}
                                                onClick={() => navigate(navPath, {
                                                    state: { from: location.pathname }
                                                })}
                                                draggable={!isMobile && !isChild && (userType === 'admin' || userType === 'super_admin')}
                                                onDragStart={(e) => {
                                                    if (isMobile || isChild || (userType !== 'admin' && userType !== 'super_admin')) {
                                                        e.preventDefault();
                                                        return;
                                                    }
                                                    const originalAssignment = {
                                                        ...row.original,
                                                        _id: jobId
                                                    };
                                                    e.dataTransfer.setData('application/json', JSON.stringify(originalAssignment));
                                                    e.dataTransfer.effectAllowed = 'move';
                                                    e.currentTarget.style.opacity = '0.6';
                                                }}
                                                onDragEnd={(e) => {
                                                    e.currentTarget.style.opacity = '1';
                                                }}
                                            >
                                                {row.getVisibleCells().map(cell => (
                                                    <td
                                                        key={cell.id}
                                                        className={`px-2 h-full content-center border-r border-gray-200/90 self-center ${isChild ? 'text-gray-600' : ''}`}
                                                        style={{
                                                            width: cell.column.getSize(),
                                                            flexShrink: 0, // ✅ VERY IMPORTANT
                                                        }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        </AssignmentContextMenu>
                                    );
                                })}
                            </>
                        )}
                    </tbody>
                </table>

                {/* Initial loading — outside tbody so it's properly centered */}
                {(isTableLoading || isLoading) && filteredAssignments.length === 0 && (
                    <div className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center justify-center gap-3">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="text-gray-600 font-medium">Loading assignments...</span>
                        </div>
                    </div>
                )}

                {/* Empty state */}
                {!isTableLoading && !isLoading && filteredAssignments.length === 0 && (
                    <div className="px-4 py-12 text-center">
                        {searchQuery ? (
                            <>
                                <FiGrid className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 font-medium">No assignments found matching "{searchQuery}"</p>
                                <p className="text-gray-400 text-sm mt-1">Try adjusting your search terms</p>
                            </>
                        ) : (
                            <>
                                <FiGrid className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 font-medium">No assignments found</p>
                                <p className="text-gray-400 text-sm mt-1">Assignments will appear here when available</p>
                            </>
                        )}
                    </div>
                )}

                {/* Loading more — outside tbody so it appears at bottom, not overlapping rows */}
                {isLoadingMore && !searchQuery && invoiceFilter === 'all' && quoteSentFilter === 'all' && (
                    <div className="px-4 py-8 text-center">
                        <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span className="text-gray-600">Loading more assignments...</span>
                        </div>
                    </div>
                )}

                {/* All loaded */}
                {!hasMore && filteredAssignments.length > 0 && !searchQuery && invoiceFilter === 'all' && quoteSentFilter === 'all' && (
                    <div className="px-4 py-4 text-center text-gray-500 text-sm">
                        All assignments loaded
                    </div>
                )}
            </div>

            {
                editOpen && editJobId && (
                    <EditAssignment
                        modalJobId={editJobId}
                        modalCompanySlug={company}
                        onClose={(saved) => {
                            setEditOpen(false);
                            setEditJobId(null);
                            if (saved) onRefresh();
                        }}
                    />
                )
            }
        </div >
    );
});

export default JobEditorTable;